/* OpenFOAM fvSchemes blocks – specialized for numerical schemes */
import * as Blockly from 'blockly';

// Constants
const PREFIX = 'fvSchemes';
const BLOCK_COLOR = 160; // Base color for fvSchemes blocks

// Consistent color scheme
const COLORS = {
  BASE: BLOCK_COLOR,              // 160 - Root blocks (dict, foamfile)
  VALUE: BLOCK_COLOR,         // 165 - Basic value blocks
  DROPDOWN: BLOCK_COLOR,      // 168 - All dropdown blocks
  GRADIENT: BLOCK_COLOR,     // 170 - Gradient schemes
  DIVERGENCE: BLOCK_COLOR,   // 180 - Divergence schemes  
  TIME_DERIV: BLOCK_COLOR,   // 190 - Time derivative schemes
  TIME_DERIV_2: BLOCK_COLOR, // 195 - Second time derivative schemes
  LAPLACIAN: BLOCK_COLOR,    // 200 - Laplacian schemes
  INTERPOLATION: BLOCK_COLOR, // 210 - Interpolation schemes
  SNGRAD: BLOCK_COLOR,       // 220 - Surface normal gradient schemes
  WALL_DIST: BLOCK_COLOR     // 230 - Wall distance schemes
};

// Default FoamFile entries for fvSchemes
const DEFAULT_FOAMFILE_ENTRIES = {
  version: '2.0',
  format: 'ascii',
  class: 'dictionary',
  location: 'system',
  object: 'fvSchemes'
};

// Define validation functions for different scheme types
const VALIDATORS = {
  // Time scheme validators
  timeScheme: (value) => {
    const validSchemes = ['steadyState', 'Euler', 'backward', 'CrankNicolson', 'localEuler'];
    return validSchemes.includes(value) ? value : 'Euler';
  },
  crankNicolsonCoeff: (value) => {
    const num = Number(value);
    return !isNaN(num) && num >= 0 && num <= 1 ? num : 0.9;
  },
  
  // Gradient scheme validators
  gradScheme: (value) => {
    const validSchemes = ['Gauss linear', 'cellLimited Gauss linear 1', 'leastSquares', 'Gauss cubic'];
    return validSchemes.includes(value) ? value : 'Gauss linear';
  },
  
  // Divergence scheme validators  
  divScheme: (value) => {
    const validSchemes = [
      'none',
      // Basic schemes
      'Gauss linear', 
      'Gauss upwind',
      'bounded Gauss linear',
      'bounded Gauss upwind',
      // NVD/TVD convection schemes
      'Gauss limitedLinear', 
      'Gauss linearUpwind', 
      'Gauss vanLeer',
      'Gauss QUICK',
      'Gauss UMIST',
      'Gauss MUSCL',
      'Gauss minmod',
      'Gauss midPoint',
      // Non-NVD/TVD convection schemes
      'Gauss LUST', 
      'Gauss filteredLinear2',
      'Gauss CBlended',
      'Gauss DESHybrid',
      // With grad references
      'Gauss linearUpwind grad(U)', 
      'Gauss LUST grad(U)', 
      'Gauss LUST unlimitedGrad(U)',
      // Bounded versions
      'bounded Gauss linearUpwind grad(U)',
      'bounded Gauss limitedLinear',
      'bounded Gauss vanLeer',
      // V schemes for vector fields  
      'Gauss linearV',
      'Gauss limitedLinearV',
      'Gauss linearUpwindV',
      'bounded Gauss limitedLinearV'
    ];
    return validSchemes.includes(value) ? value : 'Gauss linear';
  },
  
  // Laplacian scheme validators
  laplacianScheme: (value) => {
    const validSchemes = [
      'Gauss linear corrected', 
      'Gauss linear limited corrected 0.33',
      'Gauss linear limited corrected 0.5', 
      'Gauss linear orthogonal',
      'Gauss linear uncorrected'
    ];
    return validSchemes.includes(value) ? value : 'Gauss linear corrected';
  },
  
  // Interpolation scheme validators
  interpolationScheme: (value) => {
    const validSchemes = ['linear', 'cubic', 'upwind', 'limitedLinear'];
    return validSchemes.includes(value) ? value : 'linear';
  },
  
  // Surface normal gradient scheme validators
  snGradScheme: (value) => {
    const validSchemes = [
      'corrected', 'limited corrected 0.33', 'limited corrected 0.5',
      'orthogonal', 'uncorrected'
    ];
    return validSchemes.includes(value) ? value : 'corrected';
  }
};

// Define scheme configurations for different categories (simplified - no default-specific blocks)
const SCHEME_CONFIGS = {
  // Time derivative schemes (ddtSchemes) - using generic section blocks
  ddtSchemes: [],
  
  // Second time derivative schemes (d2dt2Schemes) - using generic section blocks
  d2dt2Schemes: [],
  
  // Gradient schemes - simplified to one generic entry
  gradSchemes: [
    { 
      key: 'grad', 
      shadow: `${PREFIX}_value_string`, 
      check: ['String', 'ValueList'], 
      tooltip: 'Generic gradient scheme (can accept single value or valueList)',
      validator: VALIDATORS.gradScheme,
      defaultValue: 'cellLimited Gauss linear 1',
      isGeneric: true,
      schemeType: 'gradient'
    }
  ],
  
  // Divergence schemes - simplified to one generic entry
  divSchemes: [
    { 
      key: 'div', 
      shadow: `${PREFIX}_value_string`, 
      check: ['String', 'ValueList'], 
      tooltip: 'Generic divergence scheme (can accept single value or valueList)',
      validator: VALIDATORS.divScheme,
      defaultValue: 'Gauss linearUpwind grad(U)',
      isGeneric: true,
      schemeType: 'divergence'
    }
  ],
  
  // Laplacian schemes - using generic section blocks
  laplacianSchemes: [],
  
  // Interpolation schemes - using generic section blocks
  interpolationSchemes: [],
  
  // Surface normal gradient schemes - using generic section blocks
  snGradSchemes: [],

  // Wall distance calculation schemes
  wallDist: [
    { 
      key: 'method', 
      shadow: `${PREFIX}_value_string`, 
      check: 'String', 
      tooltip: 'Wall distance calculation method',
      validator: (value) => {
        const validMethods = ['meshWave', 'Poisson', 'advectionDiffusion'];
        return validMethods.includes(value) ? value : 'meshWave';
      },
      defaultValue: 'meshWave'
    }
  ]
};

/**
 * Utility to create and initialize a Blockly block
 */
function createBlock(workspace, blockType) {
  try {
    if (!Blockly.Blocks[blockType]) {
      throw new Error(`Block type ${blockType} not defined`);
    }
    const block = workspace.newBlock(blockType);
    block.initSvg();
    block.render();
    return block;
  } catch (error) {
    console.error(`Error creating block ${blockType}:`, error);
    return null;
  }
}

/**
 * Utility to connect a value block to a block's input
 */
function connectValueBlock(block, valueBlock, entryKey) {
  try {
    const valueInput = block.getInput('VALUE') || block.getInput(entryKey);
    if (!valueInput) {
      console.warn(`No VALUE or ${entryKey} input found for block with key ${entryKey}`);
      return false;
    }

    if (!valueBlock || !valueBlock.outputConnection) {
      console.warn(`No valid output connection for value block of ${entryKey}`);
      return false;
    }

    const originalCheck = valueInput.connection.getCheck();
    valueInput.connection.setCheck(null);
    valueInput.connection.connect(valueBlock.outputConnection);
    valueInput.connection.setCheck(originalCheck);
    console.log(`Connected value block for ${entryKey}`);
    return true;
  } catch (error) {
    console.error(`Failed to connect value block for ${entryKey}:`, error);
    return false;
  }
}

// Register all blocks for this dictionary
export function registerBlocks(params = {}) {
  try {
    const filename = params.filename || 'fvSchemes';
    
    console.log(`Registering fvSchemes-specific blocks for ${filename}`);
    registerRootBlock(filename);
    registerSchemeBlocks(filename);
    registerSchemeSectionBlocks(filename);
    registerFoamFileBlock(filename);
    registerSchemeValueBlocks(filename);
    registerSpecializedKeywordBlocks(filename);
    registerGenericSectionBlocks(filename);  // Add generic section blocks
    registerParameterBlocks(filename);
    registerValueListBlocks(filename);
    console.log(`Registered ${filename} blocks:`, Object.keys(Blockly.Blocks).filter(type => type.startsWith(PREFIX)));
  } catch (error) {
    console.error(`Error registering blocks for ${params.filename || 'fvSchemes'}:`, error);
  }
}

// Register the root block
function registerRootBlock(filename) {
  const id = `${PREFIX}_dict`;
  if (Blockly.Blocks[id]) {
    console.log(`Root block ${id} already registered`);
    return;
  }

  Blockly.Blocks[id] = {
    init: function() {
      this.filename = filename;
      this.jsonInit({
        type: id,
        message0: '%1 %2',
        args0: [
          {
            type: 'field_label',
            name: 'DICT_NAME',
            text: filename
          },
          {
            type: 'field_label',
            name: 'FILENAME',
            text: `(${filename})`
          }
        ],
        inputsInline: true,
        nextStatement: null,
        colour: COLORS.BASE,
        tooltip: `Root dictionary for ${filename}`,
        helpUrl: ''
      });
      
      this.appendStatementInput('ENTRIES')
        .setCheck(null)
        .appendField('entries');
    }
  };
  
  console.log(`Registered root block: ${id}`);
}

// Register scheme section blocks (ddtSchemes, gradSchemes, etc.)
function registerSchemeSectionBlocks(filename) {
  Object.keys(SCHEME_CONFIGS).forEach(sectionName => {
    const id = `${PREFIX}_${sectionName}_section`;
    if (Blockly.Blocks[id]) return;

    // Get section-specific visual indicators with consistent colors
    const getSectionInfo = function(sectionName) {
      const sectionMap = {
        ddtSchemes: {
          icon: '∂/∂t',
          color: COLORS.TIME_DERIV,
          description: 'Time Derivative Schemes'
        },
        d2dt2Schemes: {
          icon: '∂²/∂t²',
          color: COLORS.TIME_DERIV_2,
          description: 'Second Time Derivative Schemes'
        },
        gradSchemes: {
          icon: '∇',
          color: COLORS.GRADIENT,
          description: 'Gradient Schemes'
        },
        divSchemes: {
          icon: '∇·',
          color: COLORS.DIVERGENCE,
          description: 'Divergence Schemes'
        },
        laplacianSchemes: {
          icon: '∇²',
          color: COLORS.LAPLACIAN,
          description: 'Laplacian Schemes'
        },
        interpolationSchemes: {
          icon: '⟨⟩',
          color: COLORS.INTERPOLATION,
          description: 'Interpolation Schemes'
        },
        snGradSchemes: {
          icon: '∇⊥',
          color: COLORS.SNGRAD,
          description: 'Surface Normal Gradient Schemes'
        },
        wallDist: {
          icon: '📏',
          color: COLORS.WALL_DIST,
          description: 'Wall Distance Calculation'
        }
      };
      return sectionMap[sectionName] || { icon: '?', color: COLORS.BASE, description: 'Schemes' };
    };

    const sectionInfo = getSectionInfo(sectionName);

    Blockly.Blocks[id] = {
      init: function() {
        this.jsonInit({
          type: id,
          message0: '%1 %2',
          args0: [
            {
              type: 'field_label',
              name: 'SECTION_ICON',
              text: sectionInfo.icon
            },
            {
              type: 'field_label',
              name: 'SECTION_NAME',
              text: sectionName
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: sectionInfo.color,
          tooltip: `${sectionInfo.description} section containing numerical schemes`,
          helpUrl: ''
        });
        
        this.appendStatementInput('SCHEMES')
          .setCheck(null)
          .appendField('schemes');
      }
    };
    
    console.log(`Registered scheme section block: ${id}`);
  });
}

// Register individual scheme blocks
function registerSchemeBlocks(filename) {
  Object.entries(SCHEME_CONFIGS).forEach(([sectionName, schemes]) => {
    schemes.forEach(config => {
      const safeKey = config.key.replace(/[^a-zA-Z0-9]/g, '_');
      const id = `${PREFIX}_${sectionName}_${safeKey}`;
      
      if (Blockly.Blocks[id]) return;

      Blockly.Blocks[id] = {
        init: function() {
          this.jsonInit({
            type: id,
            message0: '%1 %2',
            args0: [
              {
                type: 'field_label',
                name: 'KEY',
                text: config.key
              },
              {
                type: 'input_value',
        name: 'VALUE',
                check: config.check
              }
            ],
            inputsInline: true,
            previousStatement: null,
            nextStatement: null,
            colour: COLORS.BASE,
            tooltip: config.tooltip,
            helpUrl: ''
          });
        }
      };
      
      console.log(`Registered scheme block: ${id}`);
    });
  });
}

function registerFoamFileBlock(filename) {
  const id = `${PREFIX}_foamfile`;
  if (Blockly.Blocks[id]) {
    console.log(`FoamFile block ${id} already registered`);
    return;
  }

  Blockly.Blocks[id] = {
    init: function() {
      this.filename = filename;  // Set filename on block
      this.jsonInit({
        type: id,
        message0: 'FoamFile',
        message1: 'version %1',
        args1: [
          {
            type: 'input_value',
            name: 'VERSION',
            check: 'String'
          }
        ],
        message2: 'format %1',
        args2: [
          {
            type: 'input_value',
            name: 'FORMAT',
            check: 'String'
          }
        ],
        message3: 'class %1',
        args3: [
          {
            type: 'input_value',
            name: 'CLASS',
            check: 'String'
          }
        ],
        message4: 'location %1',
        args4: [
          {
            type: 'input_value',
            name: 'LOCATION',
            check: 'String'
          }
        ],
        message5: 'object %1',
        args5: [
          {
            type: 'input_value',
            name: 'OBJECT',
            check: 'String'
          }
        ],
        previousStatement: null,
        nextStatement: null,
        colour: COLORS.BASE,
        tooltip: `FoamFile dictionary for ${filename}`,
        helpUrl: '',
      });

      // Set shadow blocks using DOMParser
      const fields = ['VERSION', 'FORMAT', 'CLASS', 'LOCATION', 'OBJECT'];
      const parser = new DOMParser();
      fields.forEach(field => {
        const input = this.getInput(field);
        if (input) {
          const shadowXmlString = `
            <shadow type="${PREFIX}_value_string">
              <field name="VALUE">${DEFAULT_FOAMFILE_ENTRIES[field.toLowerCase()]}</field>
            </shadow>
          `;
          const shadowXml = parser.parseFromString(shadowXmlString, 'text/xml').documentElement;
          input.connection.setShadowDom(shadowXml);
          // Set filename on shadow block
          const shadowBlock = input.connection.targetBlock();
          if (shadowBlock) {
            shadowBlock.filename = filename;
          }
        }
      });
    },
  };

  console.log(`Registered FoamFile block: ${id} for ${filename}`);
}
// Register specialized scheme value blocks
function registerSchemeValueBlocks(filename) {
  // fvSchemes-specific number value block
  const numberValueId = `${PREFIX}_value_number`;
  if (!Blockly.Blocks[numberValueId]) {
    Blockly.Blocks[numberValueId] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: numberValueId,
          message0: '# %1',
          args0: [
            {
              type: 'field_number',
              name: 'VALUE',
              value: 0,
              precision: 0.001
            }
          ],
          output: 'Number',
          colour: COLORS.VALUE,
          tooltip: `Numeric value for ${filename} schemes`,
          editable: true
        });
      }
    };
    console.log(`Registered fvSchemes number value block: ${numberValueId}`);
  }

  // Wall distance calculation schemes
  const wallDistId = `${PREFIX}_walldist_section`;
  if (!Blockly.Blocks[wallDistId]) {
    Blockly.Blocks[wallDistId] = {
      init: function() {
        this.jsonInit({
          type: wallDistId,
          message0: 'wallDist',
          previousStatement: null,
          nextStatement: null,
          colour: COLORS.WALL_DIST,
          tooltip: 'Wall distance calculation method',
          helpUrl: ''
        });
        
        this.appendValueInput('METHOD')
          .setCheck('String')
          .appendField('method');
      }
    };
    console.log(`Registered wallDist section block: ${wallDistId}`);
  }

  
  // Register four-part multi-value block for schemes like "Gauss linear limited corrected 0.33"
  const multiValueFourType = `${PREFIX}_multi_value_four`;
  if (!Blockly.Blocks[multiValueFourType]) {
    Blockly.Blocks[multiValueFourType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: multiValueFourType,
          message0: '%1 %2 %3 %4',
          args0: [
            {
              type: 'input_value',
              name: 'FIRST_VALUE',
              check: ['String']
            },
            {
              type: 'input_value',
              name: 'SECOND_VALUE',
              check: ['String']
            },
            {
              type: 'input_value',
              name: 'THIRD_VALUE',
              check: ['String']
            },
            {
              type: 'input_value',
              name: 'FOURTH_VALUE',
              check: ['String', 'Number']
            }
          ],
          output: 'String',
          colour: COLORS.VALUE,
          tooltip: 'Four-part scheme value (e.g., "Gauss linear limited corrected 0.33")',
          inputsInline: true
        });
      }
    };
  }
}

// Register generic section-based keyValue blocks for dynamic keys
function registerGenericSectionBlocks(filename) {
  // For each scheme section, register a generic keyValue block that can handle any key
  Object.keys(SCHEME_CONFIGS).forEach(sectionName => {
    const genericBlockId = `${PREFIX}_keyword_${sectionName}_generic`;
    
    if (!Blockly.Blocks[genericBlockId]) {
      Blockly.Blocks[genericBlockId] = {
        init: function() {
          this.filename = filename;
          
          // Get scheme-specific visual indicators with consistent colors
          const schemeInfo = this.getSchemeInfoBySection(sectionName);
          
          this.jsonInit({
            type: genericBlockId,
            message0: '%1 %2 %3',
            args0: [
              {
                type: 'field_label',
                name: 'SCHEME_ICON',
                text: schemeInfo.icon
              },
              {
                type: 'field_input',
                name: 'KEY',
                text: 'key',
                validator: function(newValue) {
                  // Allow any valid OpenFOAM key name
                  return newValue || 'key';
                }
              },
              {
                type: 'input_value',
                name: 'VALUE',
                check: ['String', 'ValueList']
              }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: schemeInfo.color,
            tooltip: `${schemeInfo.description}: Generic key-value entry for ${sectionName} (${filename})`,
            inputsInline: true
          });
          
          console.log(`Created generic section block ${genericBlockId} for ${sectionName}`);
        },
        
        getSchemeInfoBySection: function(sectionName) {
          const sectionMap = {
            ddtSchemes: {
              icon: '∂/∂t',
              color: COLORS.TIME_DERIV,
              description: 'Time Derivative Scheme'
            },
            d2dt2Schemes: {
              icon: '∂²/∂t²',
              color: COLORS.TIME_DERIV_2,
              description: 'Second Time Derivative Scheme'
            },
            gradSchemes: {
              icon: '∇',
              color: COLORS.GRADIENT,
              description: 'Gradient Scheme'
            },
            divSchemes: {
              icon: '∇·',
              color: COLORS.DIVERGENCE,
              description: 'Divergence Scheme'
            },
            laplacianSchemes: {
              icon: '∇²',
              color: COLORS.LAPLACIAN,
              description: 'Laplacian Scheme'
            },
            interpolationSchemes: {
              icon: '⟨⟩',
              color: COLORS.INTERPOLATION,
              description: 'Interpolation Scheme'
            },
            snGradSchemes: {
              icon: '∇⊥',
              color: COLORS.SNGRAD,
              description: 'Surface Normal Gradient Scheme'
            },
            wallDist: {
              icon: '📏',
              color: COLORS.WALL_DIST,
              description: 'Wall Distance Method'
            }
          };
          return sectionMap[sectionName] || { icon: '?', color: COLORS.BASE, description: 'Scheme' };
        }
      };
      
      console.log(`Registered generic section block: ${genericBlockId} for ${sectionName}`);
    }
  });
}

// Register specialized keyword blocks for fvSchemes (simplified with generic grad/div)
function registerSpecializedKeywordBlocks(filename) {
  // Register fvSchemes-specific value blocks first
  const valueStringType = `${PREFIX}_value_string`;
  if (!Blockly.Blocks[valueStringType]) {
    Blockly.Blocks[valueStringType] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: valueStringType,
          message0: '❝ %1 ❞',
          args0: [
            {
              type: 'field_input',
              name: 'VALUE',
              text: '',
              validator: function(newValue) {
                // Basic validation for scheme syntax
                return newValue;
              }
            }
          ],
          output: 'String',
          colour: COLORS.VALUE,
          tooltip: `Scheme value for ${filename}`,
          editable: true
        });
      }
    };
  }

  // Create specialized blocks for each scheme category and key
  Object.entries(SCHEME_CONFIGS).forEach(([sectionName, schemes]) => {
    schemes.forEach(config => {
      const safeKey = config.key.replace(/[^a-zA-Z0-9]/g, '_');
      const keywordId = `${PREFIX}_keyword_${sectionName}_${safeKey}`;
      
      if (Blockly.Blocks[keywordId]) return;

      // Create the main keyword block with enhanced functionality for generic blocks
      Blockly.Blocks[keywordId] = {
        init: function() {
          this.filename = filename;
          
          // Enhanced initialization for generic blocks (grad/div)
          if (config.isGeneric) {
            // Get scheme-specific visual indicators with consistent colors
            const schemeInfo = this.getSchemeInfo(config.schemeType);
            
            this.jsonInit({
              type: keywordId,
              message0: '%1 %2 ( %3 ) %4',
              args0: [
                {
                  type: 'field_label',
                  name: 'SCHEME_ICON',
                  text: schemeInfo.icon
                },
                {
                  type: 'field_dropdown',
                  name: 'SCHEME_TYPE',
                  options: this.getSchemeTypeOptions(config.schemeType)
                },
                {
                  type: 'field_input',
                  name: 'VARIABLE',
                  text: config.schemeType === 'gradient' ? 'U' : 'phi,U',
                  validator: function(newValue) {
                    // Allow variables like U, p, k, phi,U, etc.
                    return newValue || (config.schemeType === 'gradient' ? 'U' : 'phi,U');
                  }
                },
                {
                  type: 'input_value',
                  name: 'VALUE',
                  check: ['String', 'ValueList']
                }
              ],
              previousStatement: null,
              nextStatement: null,
              colour: schemeInfo.color,
              tooltip: `${schemeInfo.description}: ${config.tooltip}`,
              inputsInline: true
            });
            
            // Set default scheme type
            this.setFieldValue(config.key, 'SCHEME_TYPE');
          } else {
            // Standard keyword block for non-generic entries with type indicators
            const schemeInfo = this.getSchemeInfoBySection(sectionName);
            
            this.jsonInit({
              type: keywordId,
              message0: '%1 %2 %3',
              args0: [
                {
                  type: 'field_label',
                  name: 'SCHEME_ICON',
                  text: schemeInfo.icon
                },
                {
                  type: 'field_input',
                  name: 'KEY',
                  text: config.key
                },
                {
                  type: 'input_value',
                  name: 'VALUE',
                  check: ['String', 'ValueList']
                }
              ],
              previousStatement: null,
              nextStatement: null,
              colour: schemeInfo.color,
              tooltip: `${schemeInfo.description}: ${config.tooltip} (${filename})`,
              inputsInline: true
            });
          }
          
          console.log(`Created ${config.isGeneric ? 'generic' : 'standard'} keyword block ${keywordId}`);
        },
        
        getSchemeInfo: function(schemeType) {
          const schemeMap = {
            gradient: {
              icon: '∇',
              color: COLORS.GRADIENT,
              description: 'Gradient Scheme'
            },
            divergence: {
              icon: '∇·',
              color: COLORS.DIVERGENCE,
              description: 'Divergence Scheme'
            }
          };
          return schemeMap[schemeType] || { icon: '?', color: COLORS.BASE, description: 'Unknown Scheme' };
        },
        
        getSchemeInfoBySection: function(sectionName) {
          const sectionMap = {
            ddtSchemes: {
              icon: '∂/∂t',
              color: COLORS.TIME_DERIV,
              description: 'Time Derivative Scheme'
            },
            d2dt2Schemes: {
              icon: '∂²/∂t²',
              color: COLORS.TIME_DERIV_2,
              description: 'Second Time Derivative Scheme'
            },
            gradSchemes: {
              icon: '∇',
              color: COLORS.GRADIENT,
              description: 'Gradient Scheme'
            },
            divSchemes: {
              icon: '∇·',
              color: COLORS.DIVERGENCE,
              description: 'Divergence Scheme'
            },
            laplacianSchemes: {
              icon: '∇²',
              color: COLORS.LAPLACIAN,
              description: 'Laplacian Scheme'
            },
            interpolationSchemes: {
              icon: '⟨⟩',
              color: COLORS.INTERPOLATION,
              description: 'Interpolation Scheme'
            },
            snGradSchemes: {
              icon: '∇⊥',
              color: COLORS.SNGRAD,
              description: 'Surface Normal Gradient Scheme'
            },
            wallDist: {
              icon: '📏',
              color: COLORS.WALL_DIST,
              description: 'Wall Distance Method'
            }
          };
          return sectionMap[sectionName] || { icon: '?', color: COLORS.BASE, description: 'Scheme' };
        },
        
        getSchemeTypeOptions: function(schemeType) {
          if (schemeType === 'gradient') {
            return [
              ['grad', 'grad'],
              ['cellLimitedGrad', 'cellLimitedGrad'],
              ['limitedGrad', 'limitedGrad']
            ];
          } else if (schemeType === 'divergence') {
            return [
              ['div', 'div'],
              ['boundedDiv', 'boundedDiv']
            ];
          }
          return [['default', 'default']];
        }
      };
      
      console.log(`Registered specialized keyword block: ${keywordId} for ${filename}`);
    });
  });
}

// Register specialized parameter blocks for complex schemes
function registerParameterBlocks(filename) {
  // Interpolation scheme parameter block
  const interpParamId = `${PREFIX}_interpolation_param`;
  if (!Blockly.Blocks[interpParamId]) {
    Blockly.Blocks[interpParamId] = {
      init: function() {
        this.jsonInit({
          type: interpParamId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'INTERPOLATION',
              options: [
                ['linear', 'linear'],
                ['linearUpwind', 'linearUpwind'],
                ['linearUpwindV', 'linearUpwindV'],
                ['upwind', 'upwind'],
                ['limitedLinear', 'limitedLinear'],
                ['limitedLinearV', 'limitedLinearV'],
                ['LUST', 'LUST'],
                ['cubic', 'cubic']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.DROPDOWN,
          tooltip: 'Interpolation scheme parameter'
        });
      }
    };
  }

  // Coefficient parameter block
  const coeffParamId = `${PREFIX}_coefficient_param`;
  if (!Blockly.Blocks[coeffParamId]) {
    Blockly.Blocks[coeffParamId] = {
      init: function() {
        this.jsonInit({
          type: coeffParamId,
          message0: '%1',
          args0: [
            {
              type: 'field_number',
              name: 'COEFFICIENT',
              value: 0.33,
              min: 0,
              max: 1,
              precision: 0.01
            }
          ],
          output: 'String',
          colour: COLORS.DROPDOWN,
          tooltip: 'Coefficient parameter (0-1)'
        });
      }
    };
  }

  // Gradient reference parameter block
  const gradRefId = `${PREFIX}_gradient_reference`;
  if (!Blockly.Blocks[gradRefId]) {
    Blockly.Blocks[gradRefId] = {
      init: function() {
        this.jsonInit({
          type: gradRefId,
          message0: '%1(%2)',
          args0: [
            {
              type: 'field_dropdown',
              name: 'FUNCTION',
              options: [
                ['grad', 'grad'],
                ['unlimitedGrad', 'unlimitedGrad']
              ]
            },
            {
              type: 'field_dropdown',
              name: 'VARIABLE',
              options: [
                ['U', 'U'],
                ['p', 'p'],
                ['k', 'k'],
                ['omega', 'omega'],
                ['epsilon', 'epsilon'],
                ['nuTilda', 'nuTilda']
              ]
            }
          ],
          output: 'FunctionCall',
          colour: COLORS.DROPDOWN,
          tooltip: 'Gradient function reference'
        });
      }
    };
  }
}

// Register valueList support blocks for fvSchemes
function registerValueListBlocks(filename) {
  // Register scheme-specific value blocks that can handle multiple parts
  
  // Gradient value block (up to 5 parts, can leave empty)
  const gradientValueId = `${PREFIX}_gradient_value`;
  if (!Blockly.Blocks[gradientValueId]) {
    Blockly.Blocks[gradientValueId] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: gradientValueId,
          message0: '∇ %1 %2 %3 %4 %5',
          args0: [
            {
              type: 'input_value',
              name: 'PART1',
              check: 'String'
            },
            {
              type: 'input_value', 
              name: 'PART2',
              check: 'String'
            },
            {
              type: 'input_value',
              name: 'PART3',
              check: ['String', 'Number']
            },
            {
              type: 'input_value',
              name: 'PART4',
              check: ['String', 'Number']
            },
            {
              type: 'input_value',
              name: 'PART5',
              check: ['String', 'Number']
            }
          ],
          output: 'ValueList',
          colour: COLORS.GRADIENT,
          tooltip: 'Gradient scheme value (up to 5 parts, leave unused parts empty)',
          inputsInline: true
        });
      }
    };
  }

  // Divergence value block (up to 5 parts, can leave empty)
  const divergenceValueId = `${PREFIX}_divergence_value`;
  if (!Blockly.Blocks[divergenceValueId]) {
    Blockly.Blocks[divergenceValueId] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: divergenceValueId,
          message0: '∇· %1 %2 %3 %4 %5',
          args0: [
            {
              type: 'input_value',
              name: 'PART1',
              check: 'String'
            },
            {
              type: 'input_value',
              name: 'PART2',
              check: 'String'
            },
            {
              type: 'input_value',
              name: 'PART3',
              check: ['String', 'Number']
            },
            {
              type: 'input_value',
              name: 'PART4',
              check: ['String', 'Number']
            },
            {
              type: 'input_value',
              name: 'PART5',
              check: ['String', 'Number']
            }
          ],
          output: 'ValueList',
          colour: COLORS.DIVERGENCE,
          tooltip: 'Divergence scheme value (up to 5 parts, leave unused parts empty)',
          inputsInline: true
        });
      }
    };
  }

  // Time derivative value block (up to 5 parts, can leave empty)
  const timeDerivativeValueId = `${PREFIX}_time_derivative_value`;
  if (!Blockly.Blocks[timeDerivativeValueId]) {
    Blockly.Blocks[timeDerivativeValueId] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: timeDerivativeValueId,
          message0: '∂/∂t %1 %2 %3 %4 %5',
          args0: [
            {
              type: 'input_value',
              name: 'PART1',
              check: 'String'
            },
            {
              type: 'input_value',
              name: 'PART2',
              check: 'String'
            },
            {
              type: 'input_value',
              name: 'PART3',
              check: ['String', 'Number']
            },
            {
              type: 'input_value',
              name: 'PART4',
              check: ['String', 'Number']
            },
            {
              type: 'input_value',
              name: 'PART5',
              check: ['String', 'Number']
            }
          ],
          output: 'ValueList',
          colour: COLORS.TIME_DERIV,
          tooltip: 'Time derivative scheme value (up to 5 parts, leave unused parts empty)',
          inputsInline: true
        });
      }
    };
  }

  // Second time derivative value block (up to 5 parts, can leave empty)
  const secondTimeDerivativeValueId = `${PREFIX}_second_time_derivative_value`;
  if (!Blockly.Blocks[secondTimeDerivativeValueId]) {
    Blockly.Blocks[secondTimeDerivativeValueId] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: secondTimeDerivativeValueId,
          message0: '∂²/∂t² %1 %2 %3 %4 %5',
          args0: [
            {
              type: 'input_value',
              name: 'PART1',
              check: 'String'
            },
            {
              type: 'input_value',
              name: 'PART2',
              check: 'String'
            },
            {
              type: 'input_value',
              name: 'PART3',
              check: ['String', 'Number']
            },
            {
              type: 'input_value',
              name: 'PART4',
              check: ['String', 'Number']
            },
            {
              type: 'input_value',
              name: 'PART5',
              check: ['String', 'Number']
            }
          ],
          output: 'ValueList',
          colour: COLORS.TIME_DERIV_2,
          tooltip: 'Second time derivative scheme value (up to 5 parts, leave unused parts empty)',
          inputsInline: true
        });
      }
    };
  }

  // Laplacian value block (up to 5 parts, can leave empty)
  const laplacianValueId = `${PREFIX}_laplacian_value`;
  if (!Blockly.Blocks[laplacianValueId]) {
    Blockly.Blocks[laplacianValueId] = {
      init: function() {
        this.filename = filename;
        this.jsonInit({
          type: laplacianValueId,
          message0: '∇² %1 %2 %3 %4 %5',
          args0: [
            {
              type: 'input_value',
              name: 'PART1',
              check: 'String'
            },
            {
              type: 'input_value',
              name: 'PART2',
              check: 'String'
            },
            {
              type: 'input_value',
              name: 'PART3',
              check: ['String', 'Number']
            },
            {
              type: 'input_value',
              name: 'PART4',
              check: ['String', 'Number']
            },
            {
              type: 'input_value',
              name: 'PART5',
              check: ['String', 'Number']
            }
          ],
          output: 'ValueList',
          colour: COLORS.LAPLACIAN,
          tooltip: 'Laplacian scheme value (up to 5 parts, leave unused parts empty)',
          inputsInline: true
        });
      }
    };
  }

  // Register scheme value dropdown blocks
  registerSchemeValueDropdowns(filename);
}

// Generate toolbox XML (flat list for fvSchemes - no duplicates with shadows)
function generateToolbox() {
  // Add helper function to get appropriate scheme value type
  const getSchemeValueType = function(sectionName) {
    const schemeValueMap = {
      gradSchemes: `${PREFIX}_gradient_value`,
      divSchemes: `${PREFIX}_divergence_value`,
      ddtSchemes: `${PREFIX}_time_derivative_value`,
      d2dt2Schemes: `${PREFIX}_second_time_derivative_value`,
      laplacianSchemes: `${PREFIX}_laplacian_value`,
      interpolationSchemes: `${PREFIX}_value_string`,
      snGradSchemes: `${PREFIX}_value_string`,
      wallDist: `${PREFIX}_value_string`
    };
    return schemeValueMap[sectionName] || `${PREFIX}_gradient_value`;
  };

  // Generate specialized keyword blocks (only for remaining specific schemes like grad/div)
  const allKeywordBlocks = Object.entries(SCHEME_CONFIGS).map(([sectionName, schemes]) => {
    return schemes.map(config => {
      const safeKey = config.key.replace(/[^a-zA-Z0-9]/g, '_');
      const keywordId = `${PREFIX}_keyword_${sectionName}_${safeKey}`;
      
      // Analyze default value to determine shadow type
      const defaultValue = config.defaultValue || '';
      const valueParts = defaultValue.trim().split(/\s+/);
      
      let shadowBlock = '';
      
      if (valueParts.length === 1) {
        // Single value - use simple string shadow
        shadowBlock = `
        <value name="VALUE">
          <shadow type="${PREFIX}_value_string">
            <field name="VALUE">${defaultValue}</field>
          </shadow>
        </value>`;
      } else if (valueParts.length >= 2) {
        // Multi-part value - use appropriate specialized scheme value block
        const schemeValueType = getSchemeValueType(sectionName);
        shadowBlock = `
        <value name="VALUE">
          <shadow type="${schemeValueType}">
            <value name="PART1">
              <shadow type="${sectionName === 'gradSchemes' ? `${PREFIX}_gradient_scheme_dropdown` : 
                              sectionName === 'divSchemes' ? `${PREFIX}_divergence_scheme_dropdown` :
                              sectionName === 'ddtSchemes' ? `${PREFIX}_time_scheme_dropdown` :
                              `${PREFIX}_gradient_scheme_dropdown`}">
                <field name="${sectionName === 'gradSchemes' ? 'GRADIENT_SCHEME' : 
                              sectionName === 'divSchemes' ? 'DIVERGENCE_SCHEME' :
                              sectionName === 'ddtSchemes' ? 'TIME_SCHEME' :
                              'GRADIENT_SCHEME'}">${valueParts[0] || 'Gauss'}</field>
              </shadow>
            </value>`;
        
        if (valueParts.length >= 2) {
          shadowBlock += `
            <value name="PART2">
              <shadow type="${PREFIX}_gradient_interpolation_dropdown">
                <field name="INTERPOLATION">${valueParts[1] || 'linear'}</field>
              </shadow>
            </value>`;
        }
        
        if (valueParts.length >= 3) {
          shadowBlock += `
            <value name="PART3">
              <shadow type="${PREFIX}_gradient_interpolation_dropdown">
                <field name="INTERPOLATION">${valueParts[2] || 'linear'}</field>
              </shadow>
            </value>`;
        }
        
        if (valueParts.length >= 4) {
          const fourthValue = valueParts[3];
          const isFourthValueNumber = !isNaN(parseFloat(fourthValue));
          shadowBlock += `
            <value name="PART4">
              <shadow type="${isFourthValueNumber ? `${PREFIX}_coefficient_dropdown` : `${PREFIX}_gradient_interpolation_dropdown`}">
                <field name="${isFourthValueNumber ? 'COEFFICIENT' : 'INTERPOLATION'}">${fourthValue}</field>
              </shadow>
            </value>`;
        }
        
        shadowBlock += `
          </shadow>
        </value>`;
      } else {
        // Empty or unknown - use simple string shadow
        shadowBlock = `
        <value name="VALUE">
          <shadow type="${PREFIX}_value_string">
            <field name="VALUE">${defaultValue}</field>
          </shadow>
        </value>`;
      }
      
      return `<block type="${keywordId}">${shadowBlock}</block>`;
    }).join('');
  }).join('');

  // Generate generic section blocks for all scheme sections
  const allGenericSectionBlocks = Object.keys(SCHEME_CONFIGS).map(sectionName => {
    const genericBlockId = `${PREFIX}_keyword_${sectionName}_generic`;
    return `<block type="${genericBlockId}">
      <value name="VALUE">
        <shadow type="${PREFIX}_value_string">
          <field name="VALUE">scheme</field>
        </shadow>
      </value>
    </block>`;
  }).join('');

  // Generate section blocks (one per section type)
  const allSectionBlocks = Object.keys(SCHEME_CONFIGS).map(sectionName => 
    `<block type="${PREFIX}_${sectionName}_section"></block>`
  ).join('');

  return `
<xml>
  <!-- Root blocks -->
  <block type="${PREFIX}_dict">
    <field name="DICT_NAME">fvSchemes</field>
  </block>
  
  <block type="${PREFIX}_foamfile">
    <value name="VERSION">
      <shadow type="common_value_string">
        <field name="VALUE">2.0</field>
      </shadow>
    </value>
    <value name="FORMAT">
      <shadow type="common_value_string">
        <field name="VALUE">ascii</field>
      </shadow>
    </value>
    <value name="CLASS">
      <shadow type="common_value_string">
        <field name="VALUE">dictionary</field>
      </shadow>
    </value>
    <value name="LOCATION">
      <shadow type="common_value_string">
        <field name="VALUE">system</field>
      </shadow>
    </value>
    <value name="OBJECT">
      <shadow type="common_value_string">
        <field name="VALUE">fvSchemes</field>
      </shadow>
    </value>
  </block>
  
  <!-- Scheme section blocks -->
  ${allSectionBlocks}
  
  <!-- Specialized keyword blocks (grad/div specific) -->
  ${allKeywordBlocks}
  
  <!-- Generic section blocks (for default and other keys) -->
  ${allGenericSectionBlocks}
  
  <!-- Specialized scheme value blocks -->
  
  <block type="${PREFIX}_gradient_value">
    <value name="PART1">
      <shadow type="${PREFIX}_gradient_scheme_dropdown">
        <field name="GRADIENT_SCHEME">Gauss</field>
      </shadow>
    </value>
    <value name="PART2">
      <shadow type="${PREFIX}_gradient_interpolation_dropdown">
        <field name="INTERPOLATION">linear</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_divergence_value">
    <value name="PART1">
      <shadow type="${PREFIX}_divergence_scheme_dropdown">
        <field name="DIVERGENCE_SCHEME">Gauss</field>
      </shadow>
    </value>
    <value name="PART2">
      <shadow type="${PREFIX}_divergence_interpolation_dropdown">
        <field name="INTERPOLATION">linearUpwind</field>
      </shadow>
    </value>
    <value name="PART3">
      <shadow type="${PREFIX}_gradient_reference">
        <field name="FUNCTION">grad</field>
        <field name="VARIABLE">U</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_time_derivative_value">
    <value name="PART1">
      <shadow type="${PREFIX}_time_scheme_dropdown">
        <field name="TIME_SCHEME">Euler</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_second_time_derivative_value">
    <value name="PART1">
      <shadow type="${PREFIX}_time_scheme_dropdown">
        <field name="TIME_SCHEME">Euler</field>
      </shadow>
    </value>
  </block>
  
  <block type="${PREFIX}_laplacian_value">
    <value name="PART1">
      <shadow type="${PREFIX}_gradient_scheme_dropdown">
        <field name="GRADIENT_SCHEME">Gauss</field>
      </shadow>
    </value>
    <value name="PART2">
      <shadow type="${PREFIX}_gradient_interpolation_dropdown">
        <field name="INTERPOLATION">linear</field>
      </shadow>
    </value>
    <value name="PART3">
      <shadow type="${PREFIX}_laplacian_corrector_dropdown">
        <field name="CORRECTOR">corrected</field>
      </shadow>
    </value>
  </block>
  
  <!-- Specialized dropdown blocks -->
  <block type="${PREFIX}_gradient_scheme_dropdown">
    <field name="GRADIENT_SCHEME">Gauss</field>
  </block>
  
  <block type="${PREFIX}_gradient_interpolation_dropdown">
    <field name="INTERPOLATION">linear</field>
  </block>
  
  <block type="${PREFIX}_gradient_limiter_dropdown">
    <field name="LIMITER">cellLimited</field>
  </block>
  
  <block type="${PREFIX}_divergence_scheme_dropdown">
    <field name="DIVERGENCE_SCHEME">Gauss</field>
  </block>
  
  <block type="${PREFIX}_divergence_interpolation_dropdown">
    <field name="INTERPOLATION">linear</field>
  </block>
  
  <block type="${PREFIX}_divergence_limited_dropdown">
    <field name="LIMITED_SCHEME">Linear</field>
  </block>
  
  <block type="${PREFIX}_divergence_v_scheme_dropdown">
    <field name="V_SCHEME">linear</field>
  </block>
  
  <block type="${PREFIX}_time_scheme_dropdown">
    <field name="TIME_SCHEME">Euler</field>
  </block>
  
  <block type="${PREFIX}_laplacian_corrector_dropdown">
    <field name="CORRECTOR">corrected</field>
  </block>
  
  <block type="${PREFIX}_coefficient_dropdown">
    <field name="COEFFICIENT">0.33</field>
  </block>
  
  <!-- Simple value blocks -->
  <block type="${PREFIX}_value_string">
    <field name="VALUE">scheme</field>
  </block>
  
  <block type="${PREFIX}_value_number">
    <field name="VALUE">0.33</field>
  </block>
</xml>`;
}

// Find matching block type for parsing (prefer specialized blocks over generic ones)
function findMatchingBlockType(component, dictName = 'fvSchemes', path = '') {
  if (!component || typeof component !== 'object') {
    return null;
  }

  const { type, name, value, key } = component;
  const keyName = key || name;

  console.log(`Finding matching block type for component: type=${type}, key=${keyName}, value=`, value, `at path: ${path}`);

  if (type === 'dictionary') {
    if (name === 'FoamFile') {
      console.log(`Matched FoamFile dictionary`);
      return `${PREFIX}_foamfile`;
    }
    if (Object.keys(SCHEME_CONFIGS).includes(name)) {
      console.log(`Matched scheme section: ${name}`);
      return `${PREFIX}_${name}_section`;
    }
    console.log(`Using generic dict for: ${name}`);
    return `${PREFIX}_dict`;
  }

  if (type === 'keyValue') {
    console.log(`Searching for specialized keyword block for key: ${keyName} at path: ${path}`);
    
    // First, try to determine the scheme section from path
    const pathLower = path.toLowerCase();
    let detectedSection = null;
    
    // Enhanced path-based section detection
    const sectionPatterns = {
      gradSchemes: [
        /gradschemes/i,
        /_gradient_/i,
        /keyword_gradschemes/i
      ],
      divSchemes: [
        /divschemes/i,
        /_divergence_/i,
        /keyword_divschemes/i
      ],
      ddtSchemes: [
        /ddtschemes/i,
        /_time_derivative/i,
        /keyword_ddtschemes/i
      ],
      d2dt2Schemes: [
        /d2dt2schemes/i,
        /_second_time_derivative/i,
        /keyword_d2dt2schemes/i
      ],
      laplacianSchemes: [
        /laplacianschemes/i,
        /_laplacian/i,
        /keyword_laplacianschemes/i
      ],
      interpolationSchemes: [
        /interpolationschemes/i,
        /_interpolation/i,
        /keyword_interpolationschemes/i
      ],
      snGradSchemes: [
        /sngradschemes/i,
        /_sngrad/i,
        /keyword_sngradschemes/i
      ]
    };
    
    // Detect section from path
    for (const [sectionName, patterns] of Object.entries(sectionPatterns)) {
      for (const pattern of patterns) {
        if (pattern.test(pathLower)) {
          detectedSection = sectionName;
          console.log(`Path indicates ${sectionName} section at path: ${path}`);
          break;
        }
      }
      if (detectedSection) break;
    }
    
    // If we detected a section from path, look for specialized blocks in that section
    if (detectedSection) {
      console.log(`Looking for specialized blocks in detected section: ${detectedSection}`);
      
      // Check if this key matches any scheme in the detected section
      const sectionSchemes = SCHEME_CONFIGS[detectedSection] || [];
      const matchingScheme = sectionSchemes.find(config => config.key === keyName);
      
      if (matchingScheme) {
        const safeKey = matchingScheme.key.replace(/[^a-zA-Z0-9]/g, '_');
        const specializedBlockType = `${PREFIX}_keyword_${detectedSection}_${safeKey}`;
        
        // Check if the specialized block exists
        if (Blockly.Blocks[specializedBlockType]) {
          console.log(`Found path-based specialized keyword block for ${keyName} in ${detectedSection}: ${specializedBlockType}`);
          return specializedBlockType;
        } else {
          console.log(`Path-based specialized block ${specializedBlockType} not found in registry`);
        }
      }
      
      // For all keys (including default), use the generic section block
      console.log(`Using generic section block for key "${keyName}" in ${detectedSection}`);
      const genericBlockType = `${PREFIX}_keyword_${detectedSection}_generic`;
      
      if (Blockly.Blocks[genericBlockType]) {
        console.log(`Found generic section block: ${genericBlockType}`);
        return genericBlockType;
      } else {
        console.warn(`Generic section block ${genericBlockType} not found - this should not happen`);
      }
    }
    
    // Fallback: Check for specialized keyword blocks by key matching (original logic)
    for (const [sectionName, schemes] of Object.entries(SCHEME_CONFIGS)) {
      const matchingScheme = schemes.find(config => config.key === keyName);
      if (matchingScheme) {
        const safeKey = keyName.replace(/[^a-zA-Z0-9]/g, '_');
        const specializedBlockType = `${PREFIX}_keyword_${sectionName}_${safeKey}`;
        
        // Check if the specialized block exists
        if (Blockly.Blocks[specializedBlockType]) {
          console.log(`Found key-based specialized keyword block for ${keyName}: ${specializedBlockType}`);
          return specializedBlockType;
        } else {
          console.log(`Key-based specialized block ${specializedBlockType} not found in registry`);
        }
        
        // Fallback to regular scheme block
        const regularBlockType = `${PREFIX}_${sectionName}_${safeKey}`;
        console.log(`Using regular scheme block: ${regularBlockType}`);
        return regularBlockType;
      }
    }
    
    // Enhanced key analysis for section detection
    if (keyName) {
      const keyLower = keyName.toLowerCase();
      
      // Analyze key patterns to determine section
      if (keyLower.includes('grad') && !keyLower.includes('sngrad')) {
        console.log(`Key ${keyName} suggests gradient scheme`);
        detectedSection = 'gradSchemes';
      } else if (keyLower.includes('div')) {
        console.log(`Key ${keyName} suggests divergence scheme`);
        detectedSection = 'divSchemes';
      } else if (keyLower.includes('ddt') && !keyLower.includes('d2dt2')) {
        console.log(`Key ${keyName} suggests time derivative scheme`);
        detectedSection = 'ddtSchemes';
      } else if (keyLower.includes('d2dt2')) {
        console.log(`Key ${keyName} suggests second time derivative scheme`);
        detectedSection = 'd2dt2Schemes';
      } else if (keyLower.includes('laplacian')) {
        console.log(`Key ${keyName} suggests laplacian scheme`);
        detectedSection = 'laplacianSchemes';
      } else if (keyLower.includes('sngrad')) {
        console.log(`Key ${keyName} suggests surface normal gradient scheme`);
        detectedSection = 'snGradSchemes';
      } else if (keyLower.includes('interpolat')) {
        console.log(`Key ${keyName} suggests interpolation scheme`);
        detectedSection = 'interpolationSchemes';
      }
      
      // If we detected a section from key analysis, try to use it
      if (detectedSection) {
        const safeKey = keyName.replace(/[^a-zA-Z0-9]/g, '_');
        const inferredBlockType = `${PREFIX}_keyword_${detectedSection}_${safeKey}`;
        
        if (Blockly.Blocks[inferredBlockType]) {
          console.log(`Using key-inferred specialized block: ${inferredBlockType}`);
          return inferredBlockType;
        } else {
          console.log(`Key-inferred block ${inferredBlockType} not found, checking for default block in section`);
          
          // Try to use the default block for this section
          const defaultBlockType = `${PREFIX}_keyword_${detectedSection}_default`;
          if (Blockly.Blocks[defaultBlockType]) {
            console.log(`Using default block for detected section: ${defaultBlockType}`);
            return defaultBlockType;
          }
        }
      }
    }
    
    // Check if the value is a valueList - this indicates we should use specialized blocks
    if (value && typeof value === 'object' && value.type === 'valueList') {
      console.log(`ValueList detected for ${keyName}, looking for specialized block`);
      
      // Try to find any matching specialized block for this key
      for (const [sectionName, schemes] of Object.entries(SCHEME_CONFIGS)) {
        const matchingScheme = schemes.find(config => config.key === keyName);
        if (matchingScheme) {
          const safeKey = keyName.replace(/[^a-zA-Z0-9]/g, '_');
          const specializedBlockType = `${PREFIX}_keyword_${sectionName}_${safeKey}`;
          console.log(`Attempting to use specialized block: ${specializedBlockType}`);
          return specializedBlockType;
        }
      }
    }
    
    // If no specialized block found, return null to allow common blocks to handle it
    console.log(`No specialized block found for ${keyName}, falling back to common blocks`);
    return null;
  }

  // Handle valueList components directly
  if (type === 'valueList') {
    console.log(`Component is valueList type, returning specialized valueList block`);
    // For valueList components, we can return a valueList block type
    // This allows the enhanced valueList creation to take place
    return `${PREFIX}_value_list`;
  }

  return null;
}

// Create specialized block instances for components
function createSpecializedBlock(workspace, component, dictName = 'fvSchemes', path = '') {
  const blockType = findMatchingBlockType(component, dictName, path);
  if (!blockType) {
    return null; // Fall back to common blocks
  }

  try {
    const block = createBlock(workspace, blockType);
    if (!block) {
      console.warn(`Failed to create specialized block of type ${blockType} at path: ${path}`);
      return null;
    }

      // Set the key field if it exists - handle both component.key and component.name
  const keyValue = component.key || component.name;
  if (keyValue && block.getField('KEY')) {
    console.log(`Setting key field to: ${keyValue} for block type: ${blockType} at path: ${path}`);
    block.setFieldValue(keyValue, 'KEY');
  }
  
  // For generic section blocks, ensure the key is set even if it's a field_input
  if (blockType.includes('_generic') && keyValue) {
    const keyField = block.getField('KEY');
    if (keyField && keyField.setValue) {
      keyField.setValue(keyValue);
      console.log(`Set input field KEY to: ${keyValue} for generic block at path: ${path}`);
    }
  }

    // Note: Value processing is now handled by the caller (createKeyValueBlock)
    // This prevents redundant processing and ensures valueList values are always handled
    console.log(`Created specialized block ${blockType} for key: ${keyValue}, value processing handled by caller at path: ${path}`);

    // Handle any additional child structures (like entries in dictionaries)
    if (component.entries && Array.isArray(component.entries)) {
      console.log(`Recursively creating child entries for specialized block at path: ${path}:`, component.entries.length);
      let currentChildBlock = null;
      
      component.entries.forEach((entry, index) => {
        const entryPath = `${path}_child_${index}`;
        const childBlock = createBlockFromComponent(workspace, entry, dictName, block, entryPath);
        if (childBlock) {
          // Find appropriate input for children (ENTRIES, SCHEMES, etc.)
          const childInput = block.getInput('ENTRIES') || block.getInput('SCHEMES') || block.getInput('ITEMS');
          if (childInput) {
            if (currentChildBlock) {
              currentChildBlock.setNext(childBlock);
            } else {
              childInput.connection.connect(childBlock.previousConnection);
            }
            currentChildBlock = childBlock;
            console.log(`Connected child block ${index} to specialized block at path: ${entryPath}`);
          }
        }
      });
    }

    return block;
  } catch (error) {
    console.error(`Error creating specialized block for component at path: ${path}:`, component, error);
    return null;
  }
}

// Create appropriate value blocks for component values
function createValueBlockForComponent(workspace, value, key, path = '') {
  console.log(`Creating value block for value:`, value, `key:`, key, `at path: ${path}`);
  
  // Handle complex object values
  if (value && typeof value === 'object' && value !== null) {
    // Handle valueList objects with enhanced construction
    if (value.type === 'valueList' && Array.isArray(value.values)) {
      console.log(`Processing valueList with ${value.values.length} values at path: ${path}:`, value.values);
      return createEnhancedValueListBlock(workspace, value, key, path);
    }
    
    // Handle objects that look like valueList but don't have the type set
    if (Array.isArray(value.values) && !value.type) {
      console.log(`Object has values array but no type, treating as valueList at path: ${path}`);
      return createEnhancedValueListBlock(workspace, { type: 'valueList', values: value.values }, key, path);
    }
    
    // Handle objects with other types - avoid infinite recursion
    if (value.type && value.type !== 'valueList') {
      console.log(`Value has type ${value.type}, delegating to createBlockFromComponent at path: ${path}`);
      // Only delegate to createBlockFromComponent for non-valueList types to avoid infinite recursion
      return createBlockFromComponent(workspace, value, 'fvSchemes', null, path);
    }
    
    // Handle objects without type but with recognizable structure
    if (value.name && value.arguments) {
      // Looks like a function call
      console.log(`Object looks like function call at path: ${path}:`, value);
      return createFunctionCallBlock(workspace, value, path);
    }
    
    // If we can't determine the object structure, log and return null
    console.warn(`Unknown object structure for value:`, value);
    return null;
  }
  
  if (typeof value === 'string') {
    // Check if it's a multi-part scheme value (legacy support)
    if (value.includes(' ')) {
      const parts = value.split(' ');
      console.log(`Converting multi-part string to enhanced valueList:`, parts);
      
      // Convert string with spaces to valueList format and use enhanced creation
      const valueListData = {
        type: 'valueList',
        values: parts
      };
      
      return createEnhancedValueListBlock(workspace, valueListData, key);
    }
    
    // Simple string value for single-part schemes
    const stringBlock = createBlock(workspace, `${PREFIX}_value_string`);
    if (stringBlock) {
      stringBlock.setFieldValue(value, 'VALUE');
    }
    return stringBlock;
  }
  
  if (typeof value === 'number') {
    const numberBlock = createBlock(workspace, 'common_value_number');
    if (numberBlock) {
      numberBlock.setFieldValue(value, 'VALUE');
    }
    return numberBlock;
  }
  
  console.log(`Could not create value block for value:`, value);
  return null;
}

// Export functions similar to controlDict pattern
export function createFvSchemesBlock(workspace, entries = [], dictName = 'fvSchemes', path = '') {
  try {
    const rootBlock = createBlock(workspace, `${PREFIX}_dict`);
    if (!rootBlock) {
      console.error('Failed to create fvSchemes root block');
      return null;
    }

    console.log(`Creating fvSchemes block for ${dictName} with ${entries.length} entries at path: ${path}`);

    if (entries.length > 0) {
      let currentBlock = null;
      
      entries.forEach((entry, index) => {
        const entryPath = `${path}_entry_${index}`;
        console.log(`Processing entry ${index} at path: ${entryPath}:`, entry);
        
        // Try to create specialized block first
        let entryBlock = createSpecializedBlock(workspace, entry, dictName, entryPath);
        
        // If no specialized block, fall back to common blocks
        if (!entryBlock) {
          console.log(`Creating common block for entry at path: ${entryPath}:`, entry.name);
          entryBlock = createBlock(workspace, 'common_entry');
          if (entryBlock && entry.name) {
            entryBlock.setFieldValue(entry.name, 'KEY');
            
            if (entry.value !== undefined) {
              const valueBlock = createValueBlockForComponent(workspace, entry.value, entry.name, `${entryPath}_value`);
              if (valueBlock) {
                connectValueBlock(entryBlock, valueBlock, 'VALUE');
              }
            }
          }
        }

        if (entryBlock) {
          const entriesInput = rootBlock.getInput('ENTRIES');
          if (entriesInput) {
            if (currentBlock) {
              currentBlock.setNext(entryBlock);
            } else {
              entriesInput.connection.connect(entryBlock.previousConnection);
            }
            currentBlock = entryBlock;
          }
        }
      });
    }

    return rootBlock;
  } catch (error) {
    console.error('Error creating fvSchemes block:', error);
    return null;
  }
}

export function createKeywordBlock(workspace, key, value, path = '') {
  try {
    // Find the appropriate specialized block type
    const component = { type: 'keyValue', name: key, value: value };
    const specializedBlock = createSpecializedBlock(workspace, component, 'fvSchemes', path);
    
    if (specializedBlock) {
      return specializedBlock;
    }

    // Fallback to common entry block
    console.log(`Creating common keyword block for ${key} at path: ${path}`);
    const entryBlock = createBlock(workspace, 'common_entry');
    if (entryBlock) {
      entryBlock.setFieldValue(key, 'KEY');
      
      if (value !== undefined) {
        const valueBlock = createValueBlockForComponent(workspace, value, key, `${path}_value`);
        if (valueBlock) {
          connectValueBlock(entryBlock, valueBlock, 'VALUE');
        }
      }
    }
    
    return entryBlock;
  } catch (error) {
    console.error(`Error creating keyword block for ${key} at path: ${path}:`, error);
    return null;
  }
}

// Export registration function
export function registerFvSchemesBlocks() {
  registerBlocks();
}

// Export dictionary name and prefix for registry
export default {
  dictName: 'fvSchemes',
  prefix: PREFIX,
  registerBlocks,
  generateToolbox,
  getBlocks: () => Object.keys(Blockly.Blocks).filter(type => type.startsWith(PREFIX)),
  findMatchingBlockType: (component, dictName, path) => findMatchingBlockType(component, dictName, path),
  createBlock: createFvSchemesBlock,
  createKeywordBlock: createKeywordBlock,
  createSpecializedBlock: createSpecializedBlock,
  createValueBlockForComponent: createValueBlockForComponent,
  createBlockFromComponent: createBlockFromComponent,
  createFvSchemesBlockRecursive: createFvSchemesBlockRecursive
};

// Utility function to recursively process all entries in a component
function processEntriesRecursively(workspace, parentBlock, entries, dictName, inputName = 'ENTRIES', path = '') {
  if (!entries || !Array.isArray(entries) || entries.length === 0) {
    console.log(`No entries to process for ${parentBlock.type} at path: ${path}`);
    return 0;
  }

  const parentInput = parentBlock.getInput(inputName);
  if (!parentInput) {
    console.warn(`No ${inputName} input found in parent block ${parentBlock.type} at path: ${path}`);
    return 0;
  }

  let currentChildBlock = null;
  let connectedCount = 0;

  console.log(`Processing ${entries.length} entries recursively for ${parentBlock.type} at path: ${path}`);

  entries.forEach((entry, index) => {
    const entryPath = `${path}_entry_${index}`;
    console.log(`Processing entry ${index} at path: ${entryPath}:`, entry);

    if (!entry || typeof entry !== 'object') {
      console.warn(`Entry ${index} is not a valid object at path: ${entryPath}:`, entry);
      return;
    }

    // Recursively create the child block
    const childBlock = createBlockFromComponent(workspace, entry, dictName, parentBlock, entryPath);

    if (childBlock) {
      try {
        if (currentChildBlock && currentChildBlock.nextConnection && childBlock.previousConnection) {
          // Connect to the previous block in the chain
          currentChildBlock.nextConnection.connect(childBlock.previousConnection);
          console.log(`Chained entry ${index} to previous entry at path: ${entryPath}`);
        } else if (parentInput.connection && childBlock.previousConnection) {
          // Connect as the first block
          parentInput.connection.connect(childBlock.previousConnection);
          console.log(`Connected entry ${index} as first entry at path: ${entryPath}`);
        } else {
          console.warn(`Could not connect entry ${index} - missing connections at path: ${entryPath}`);
        }

        currentChildBlock = childBlock;
        connectedCount++;
      } catch (error) {
        console.error(`Error connecting entry ${index} at path: ${entryPath}:`, error);
      }
    } else {
      console.warn(`Failed to create child block for entry ${index} at path: ${entryPath}`);
    }
  });

  console.log(`Successfully connected ${connectedCount}/${entries.length} entries at path: ${path}`);
  return connectedCount;
}

// Enhanced recursive block creation function
function createBlockFromComponent(workspace, component, dictName = 'fvSchemes', parent = null, path = '') {
  if (!component || typeof component !== 'object') {
    console.warn('Invalid component passed to createBlockFromComponent:', component);
    return null;
  }

  console.log(`Creating block for component:`, component.type, component.name || component.key, `at path: ${path}`);

  try {
    // Handle different component types recursively
    switch (component.type) {
      case 'dictionary':
        return createDictionaryBlock(workspace, component, dictName, path);
        
      case 'keyValue':
        return createKeyValueBlock(workspace, component, dictName, path);
        
      case 'valueList':
        return createValueListFromComponent(workspace, component, component.type, path);
        
      case 'list':
        return createListBlock(workspace, component, dictName, path);
        
      case 'functionCall':
        return createFunctionCallBlock(workspace, component, path);
        
      case 'value':
        // Handle simple value types
        return createValueBlockForComponent(workspace, component.value, component.key, path);
        
      default:
        console.log(`Unknown component type: ${component.type}, attempting specialized block creation at path: ${path}`);
        
        // For unknown types, first check if it should be treated as keyValue
        // This ensures valueList processing is never skipped
        if (component.value !== undefined || component.key || component.name) {
          console.log(`Component has value/key, treating as keyValue to ensure proper value processing at path: ${path}`);
          component.type = 'keyValue'; // Update type for consistency
          return createKeyValueBlock(workspace, component, dictName, path);
        }
        
        // Try to create a specialized block for unknown types (dictionaries, etc.)
        const specializedBlock = createSpecializedBlock(workspace, component, dictName, path);
        if (specializedBlock) {
          console.log(`Successfully created specialized block for type: ${component.type} at path: ${path}`);
          
          // Ensure any child entries are processed recursively
          if (component.entries && Array.isArray(component.entries)) {
            console.log(`Processing child entries for specialized block at path: ${path}`);
            processEntriesRecursively(workspace, specializedBlock, component.entries, dictName, 'ENTRIES', path);
          }
          
          return specializedBlock;
        }
        
        // If component has entries, treat as dictionary
        if (component.entries && Array.isArray(component.entries)) {
          console.log(`Component has entries, treating as dictionary at path: ${path}`);
          component.type = 'dictionary'; // Update type for consistency
          return createDictionaryBlock(workspace, component, dictName, path);
        }
        
        console.log(`Unable to determine how to handle component type: ${component.type} at path: ${path}`);
        return null; // Let common blocks handle it
    }
  } catch (error) {
    console.error(`Error creating block for component ${component.type} at path: ${path}:`, error);
    return null;
  }
}

// Create dictionary blocks recursively
function createDictionaryBlock(workspace, component, dictName, path = '') {
  const { name, entries = [] } = component;
  
  console.log(`Creating dictionary block for: ${name} with ${entries.length} entries at path: ${path}`);
  
  // Ensure entries is an array
  const entriesArray = Array.isArray(entries) ? entries : [];
  
  // Handle special dictionaries
  if (name === 'FoamFile') {
    const foamFileBlock = createBlock(workspace, `${PREFIX}_foamfile`);
    if (foamFileBlock) {
      console.log(`Creating FoamFile with ${entriesArray.length} entries at path: ${path}`);
      
      // Process ALL FoamFile entries recursively
      entriesArray.forEach((entry, index) => {
        const entryPath = `${path}_foamfile_${index}`;
        console.log(`Processing FoamFile entry ${index} at path: ${entryPath}:`, entry);
        
        // Handle different entry formats
        let entryKey, entryValue;
        if (entry && typeof entry === 'object') {
          entryKey = entry.key || entry.name;
          entryValue = entry.value;
          
          // If entry has type keyValue or looks like a key-value pair
          if (entry.type === 'keyValue' || (entryKey && entryValue !== undefined)) {
            const inputName = entryKey.toUpperCase();
            const input = foamFileBlock.getInput(inputName);
            
            if (input) {
              // Recursively create the value block
              const valueBlock = createBlockFromComponent(workspace, { 
                type: 'value', 
                value: entryValue,
                key: entryKey
              }, dictName, foamFileBlock, `${entryPath}_value`);
              
              if (valueBlock && valueBlock.outputConnection) {
                input.connection.connect(valueBlock.outputConnection);
                console.log(`Connected FoamFile entry ${inputName} successfully at path: ${entryPath}`);
              } else {
                console.warn(`Failed to create or connect value block for FoamFile entry: ${inputName} at path: ${entryPath}`);
              }
            } else {
              console.warn(`No input found for FoamFile entry: ${inputName} at path: ${entryPath}`);
            }
          } else {
            // Handle other entry types recursively
            console.log(`FoamFile entry ${index} has different structure, processing recursively at path: ${entryPath}`);
            const entryBlock = createBlockFromComponent(workspace, entry, dictName, foamFileBlock, entryPath);
            if (entryBlock) {
              console.log(`Successfully created block for FoamFile entry ${index} at path: ${entryPath}`);
            }
          }
        } else {
          console.warn(`FoamFile entry ${index} is not a valid object at path: ${entryPath}:`, entry);
        }
      });
    }
    return foamFileBlock;
  }
  
  // Handle scheme sections
  if (Object.keys(SCHEME_CONFIGS).includes(name)) {
    console.log(`Creating scheme section for: ${name} with ${entriesArray.length} entries at path: ${path}`);
    const sectionBlock = createBlock(workspace, `${PREFIX}_${name}_section`);
    
    if (sectionBlock) {
      // Use the utility function for consistent recursive processing
      const connectedCount = processEntriesRecursively(workspace, sectionBlock, entriesArray, dictName, 'SCHEMES', `${path}_${name}`);
      console.log(`Scheme section ${name} successfully processed ${connectedCount} entries at path: ${path}`);
    }
    return sectionBlock;
  }
  
  // Generic dictionary
  const isRootDict = name === dictName;
  const dictBlockType = isRootDict ? `${PREFIX}_dict` : 'common_dict';
  const dictBlock = createBlock(workspace, dictBlockType);
  
  if (dictBlock) {
    // Set dictionary name for non-root dictionaries
    if (!isRootDict && dictBlock.getField('DICT_NAME')) {
      dictBlock.setFieldValue(name || 'dictionary', 'DICT_NAME');
      console.log(`Set dictionary name to: ${name || 'dictionary'} at path: ${path}`);
    }
    
    // Use the utility function for consistent recursive processing
    const connectedCount = processEntriesRecursively(workspace, dictBlock, entriesArray, dictName, 'ENTRIES', `${path}_${name || 'dict'}`);
    console.log(`Dictionary ${name} successfully processed ${connectedCount} entries at path: ${path}`);
  }
  
  return dictBlock;
}

// Create keyValue blocks recursively  
function createKeyValueBlock(workspace, component, dictName, path = '') {
  const { key, name, value } = component;
  const keyName = key || name;
  
  console.log(`Creating keyValue block for key: ${keyName}, component type: ${component.type}, value:`, value, `at path: ${path}`);
  
  // FIRST: Check if the component itself is a valueList
  if (component.type === 'valueList') {
    console.log(`Component is valueList type, processing directly for key: ${keyName} at path: ${path}`);
    return createEnhancedValueListBlock(workspace, component, keyName, path);
  }
  
  // SECOND: Always process the value to ensure valueList handling is complete
  let valueBlock = null;
  if (value !== undefined) {
    const valuePath = `${path}_value`;
    console.log(`Processing value for key ${keyName} at path: ${valuePath}:`, value);
    
    if (typeof value === 'object' && value !== null && value.type) {
      // Complex value with type - handle appropriately
      console.log(`Value is complex object with type: ${value.type} at path: ${valuePath}`);
      
      if (value.type === 'functionCall') {
        console.log(`Processing functionCall for key ${keyName} at path: ${valuePath}:`, value);
        valueBlock = createFunctionCallBlock(workspace, value, valuePath);
      } else if (value.type === 'value') {
        console.log(`Processing wrapped value for key ${keyName} at path: ${valuePath}:`, value.value);
        valueBlock = createValueBlockForComponent(workspace, value.value, keyName, valuePath);
      } else {
        console.log(`Processing other complex value type ${value.type} for key ${keyName} at path: ${valuePath}`);
        valueBlock = createBlockFromComponent(workspace, value, dictName, null, valuePath);
      }
    } else {
      // Simple value or untyped object
      console.log(`Processing simple value for key ${keyName} at path: ${valuePath}:`, value);
      valueBlock = createValueBlockForComponent(workspace, value, keyName, valuePath);
    }
    
    if (!valueBlock) {
      console.warn(`Failed to create value block for key: ${keyName} at path: ${valuePath}, value:`, value);
    } else {
      console.log(`Successfully created value block for key: ${keyName} at path: ${valuePath}`);
    }
  }
  
  // THIRD: Try to create specialized block for the key-value pair
  // Enhanced component for better section detection
  const enhancedComponent = {
    ...component,
    path: path  // Add path to component for better detection
  };
  
  const specializedBlock = createSpecializedBlock(workspace, enhancedComponent, dictName, path);
  if (specializedBlock) {
    console.log(`Created specialized block for key: ${keyName} at path: ${path}`);
    
    // Connect the pre-created value block to the specialized block
    if (valueBlock) {
      const valueInput = specializedBlock.getInput('VALUE');
      if (valueInput) {
        if (valueInput.connection.isConnected()) {
          console.log(`Specialized block ${specializedBlock.type} already has value connected at path: ${path}`);
        } else {
          const connected = connectValueBlock(specializedBlock, valueBlock, 'VALUE');
          if (connected) {
            console.log(`Connected pre-processed value block to specialized block for key: ${keyName} at path: ${path}`);
          } else {
            console.warn(`Failed to connect pre-processed value block to specialized block for key: ${keyName} at path: ${path}`);
          }
        }
      } else {
        console.warn(`Specialized block ${specializedBlock.type} has no VALUE input for key: ${keyName} at path: ${path}`);
      }
    }
    
    return specializedBlock;
  }
  
  // FOURTH: Fallback to common entry block
  console.log(`Creating common entry block for key: ${keyName} at path: ${path}`);
  const entryBlock = createBlock(workspace, 'common_entry');
  if (entryBlock && keyName) {
    entryBlock.setFieldValue(keyName, 'KEY');
    console.log(`Set key field to: ${keyName} for common entry block at path: ${path}`);
    
    // Connect the pre-created value block to the common entry block
    if (valueBlock) {
      const connected = connectValueBlock(entryBlock, valueBlock, 'VALUE');
      if (connected) {
        console.log(`Connected pre-processed value block to common entry block for key: ${keyName} at path: ${path}`);
      } else {
        console.warn(`Failed to connect pre-processed value block to common entry block for key: ${keyName} at path: ${path}`);
      }
    }
  }
  
  return entryBlock;
}

// Create list blocks recursively
function createListBlock(workspace, component, dictName) {
  const { name, items = [] } = component;
  
  console.log(`Creating list block for: ${name} with ${items.length} items`);
  
  const listBlock = createBlock(workspace, 'common_list');
  if (listBlock) {
    if (name) {
      listBlock.setFieldValue(name, 'LIST_NAME');
      console.log(`Set list name to: ${name}`);
    }
    
    if (items.length > 0) {
      let currentItemBlock = null;
      
      console.log(`Processing ${items.length} list items`);
      
      // Recursively create all list items
      items.forEach((item, index) => {
        console.log(`Processing list item ${index}:`, item);
        const itemBlock = createBlockFromComponent(workspace, item, dictName, listBlock);
        if (itemBlock) {
          // Wrap in list item block if needed
          let wrappedItemBlock = itemBlock;
          if (itemBlock.type !== 'common_list_item') {
            console.log(`Wrapping item ${index} in list item block`);
            wrappedItemBlock = createBlock(workspace, 'common_list_item');
            if (wrappedItemBlock) {
              const valueInput = wrappedItemBlock.getInput('VALUE');
              if (valueInput && itemBlock.outputConnection) {
                valueInput.connection.connect(itemBlock.outputConnection);
                console.log(`Connected item ${index} to list item wrapper`);
              }
            }
          }
          
          const itemsInput = listBlock.getInput('ITEMS');
          if (itemsInput && wrappedItemBlock) {
            if (currentItemBlock) {
              // Connect to the previous item in the chain
              if (currentItemBlock.nextConnection && wrappedItemBlock.previousConnection) {
                currentItemBlock.nextConnection.connect(wrappedItemBlock.previousConnection);
              }
            } else {
              // Connect as the first item
              if (itemsInput.connection && wrappedItemBlock.previousConnection) {
                itemsInput.connection.connect(wrappedItemBlock.previousConnection);
              }
            }
            currentItemBlock = wrappedItemBlock;
            console.log(`Connected list item ${index} to list ${name}`);
          } else {
            console.warn(`Failed to connect list item ${index} - missing input or wrapper`);
          }
        } else {
          console.warn(`Failed to create item block ${index} for list ${name}`);
        }
      });
    }
  }
  
  return listBlock;
}

// Create valueList blocks (existing functionality enhanced)
function createValueListFromComponent(workspace, component, key, path) {
  return createValueBlockForComponent(workspace, component, key, path);
}

// Create function call blocks
function createFunctionCallBlock(workspace, component) {
  const { name, arguments: args = [] } = component;
  
  const functionBlock = createBlock(workspace, `${PREFIX}_gradient_reference`);
  if (functionBlock && name) {
    functionBlock.setFieldValue(name, 'FUNCTION');
    if (args.length > 0) {
      functionBlock.setFieldValue(args[0], 'VARIABLE');
    }
  }
  
  return functionBlock;
}

// Enhanced recursive entry point
export function createFvSchemesBlockRecursive(workspace, ast, dictName = 'fvSchemes', path = '') {
  console.log('Creating fvSchemes blocks recursively from AST at path:', path, ast);
  
  if (!ast || typeof ast !== 'object') {
    console.error('Invalid AST provided to createFvSchemesBlockRecursive');
    return null;
  }
  
  // Start recursive creation from the root
  return createBlockFromComponent(workspace, ast, dictName);
}

/**
 * Merge divergence scheme parts to match available dropdown options
 * Handles cases like ["bounded", "Gauss", "limitedLinear"] → ["bounded Gauss", "limitedLinear"]
 */
function mergeDivergenceSchemeValues(values) {
  if (!values || !Array.isArray(values) || values.length === 0) {
    return values;
  }
  
  console.log(`Merging divergence scheme values:`, values);
  
  const merged = [];
  let i = 0;
  
  // Check for "bounded" + scheme combinations
  if (i < values.length && values[i] === 'bounded') {
    if (i + 1 < values.length && values[i + 1] === 'Gauss') {
      // Merge "bounded" + "Gauss" → "bounded Gauss"
      merged.push('bounded Gauss');
      i += 2; // Skip both parts
      console.log(`Merged "bounded" + "Gauss" → "bounded Gauss"`);
    } else {
      // Keep "bounded" as is if not followed by "Gauss"
      merged.push(values[i]);
      i++;
    }
  } else if (i < values.length && values[i] === 'Gauss') {
    // Just "Gauss" without "bounded"
    merged.push('Gauss');
    i++;
  } else if (i < values.length) {
    // Other first value
    merged.push(values[i]);
    i++;
  }
  
  // Add remaining values as-is
  while (i < values.length) {
    merged.push(values[i]);
    i++;
  }
  
  console.log(`Merged divergence scheme values:`, merged);
  return merged;
}

/**
 * Parse divergence scheme values to extract OpenFOAM divergence scheme structure
 * Expected format: <bounded> <scheme> <interpolation> <gradient_reference>
 * e.g., ["bounded", "Gauss", "limitedLinear"] → ["bounded Gauss", "limitedLinear"]
 * or ["Gauss", "linearUpwind", "grad(U)"] → ["Gauss", "linearUpwind", "grad(U)"]
 */
function parseDivergenceScheme(values) {
  const scheme = {
    divergenceScheme: null,
    interpolationScheme: null,
    gradientReference: null,
    coefficient: null,
    isLimited: false,
    isVScheme: false
  };
  
  if (!values || values.length === 0) {
    return scheme;
  }
  
  // First merge multi-part scheme values
  const mergedValues = mergeDivergenceSchemeValues(values);
  console.log(`Processing merged divergence scheme values:`, mergedValues);
  
  let currentIndex = 0;
  
  // Check for divergence scheme (first part, could be "Gauss" or "bounded Gauss")
  if (currentIndex < mergedValues.length) {
    const firstPart = mergedValues[currentIndex];
    if (firstPart === 'Gauss' || firstPart === 'bounded Gauss') {
      scheme.divergenceScheme = firstPart;
      currentIndex++;
    } else {
      // Unknown scheme, but still use it
      scheme.divergenceScheme = firstPart;
      currentIndex++;
    }
  }
  
  // Check for interpolation scheme (second part)
  if (currentIndex < mergedValues.length) {
    const secondPart = mergedValues[currentIndex];
    
    // Check if it's a limited scheme (like "limitedLinear")
    if (secondPart.startsWith('limited')) {
      scheme.isLimited = true;
      // Extract the base scheme from "limitedLinear" → "Linear"
      scheme.interpolationScheme = secondPart.replace('limited', '');
      // Capitalize first letter for dropdown compatibility
      scheme.interpolationScheme = scheme.interpolationScheme.charAt(0).toUpperCase() + 
                                   scheme.interpolationScheme.slice(1);
      currentIndex++;
    }
    // Check if it's a V-scheme (like "linearV")
    else if (secondPart.endsWith('V')) {
      scheme.isVScheme = true;
      // Extract the base scheme from "linearV" → "linear"
      scheme.interpolationScheme = secondPart.replace('V', '');
      currentIndex++;
    }
    // Regular interpolation scheme
    else {
      scheme.interpolationScheme = secondPart;
      currentIndex++;
    }
  }
  
  // Check for gradient reference (third part, like "grad(U)")
  if (currentIndex < mergedValues.length) {
    const thirdPart = mergedValues[currentIndex];
    if (thirdPart.includes('grad(') || thirdPart.includes('unlimitedGrad(')) {
      scheme.gradientReference = thirdPart;
      currentIndex++;
    } else {
      // Might be additional interpolation or coefficient
      const isNumeric = !isNaN(parseFloat(thirdPart)) && isFinite(thirdPart);
      if (isNumeric) {
        scheme.coefficient = parseFloat(thirdPart);
      } else {
        // Additional interpolation parameter
        if (!scheme.interpolationScheme) {
          scheme.interpolationScheme = thirdPart;
        }
      }
      currentIndex++;
    }
  }
  
  // Check for coefficient (usually numeric, at the end)
  if (currentIndex < mergedValues.length) {
    const lastValue = mergedValues[currentIndex];
    if (!isNaN(parseFloat(lastValue)) && isFinite(lastValue)) {
      scheme.coefficient = parseFloat(lastValue);
    }
  }
  
  console.log(`Parsed divergence scheme:`, scheme);
  return scheme;
}

/**
 * Create a structured divergence scheme block following OpenFOAM format
 */
function createDivergenceSchemeBlock(workspace, divergenceScheme, key, path) {
  console.log(`Creating structured divergence scheme block:`, divergenceScheme);
  
  // Create the main divergence value block
  const divergenceBlock = createBlock(workspace, `${PREFIX}_divergence_value`);
  if (!divergenceBlock) {
    console.error(`Failed to create divergence value block`);
    return null;
  }
  
  const inputNames = ['PART1', 'PART2', 'PART3', 'PART4', 'PART5'];
  let currentInputIndex = 0;
  
  // Add divergence scheme (required)
  if (divergenceScheme.divergenceScheme && currentInputIndex < inputNames.length) {
    const schemeBlock = createBlock(workspace, `${PREFIX}_divergence_scheme_dropdown`);
    if (schemeBlock) {
      const field = schemeBlock.getField('DIVERGENCE_SCHEME');
      if (field) {
        field.setValue(divergenceScheme.divergenceScheme);
      }
      const input = divergenceBlock.getInput(inputNames[currentInputIndex]);
      if (input && input.connection) {
        input.connection.connect(schemeBlock.outputConnection);
        console.log(`Connected divergence scheme block: ${divergenceScheme.divergenceScheme}`);
      }
    }
    currentInputIndex++;
  }
  
  // Add interpolation scheme if present
  if (divergenceScheme.interpolationScheme && currentInputIndex < inputNames.length) {
    let interpolationBlock;
    
    if (divergenceScheme.isLimited) {
      // Use limited dropdown for schemes like "limitedLinear"
      interpolationBlock = createBlock(workspace, `${PREFIX}_divergence_limited_dropdown`);
      if (interpolationBlock) {
        const field = interpolationBlock.getField('LIMITED_SCHEME');
        if (field) {
          field.setValue(divergenceScheme.interpolationScheme);
        }
      }
    } else if (divergenceScheme.isVScheme) {
      // Use V-scheme dropdown for schemes like "linearV"
      interpolationBlock = createBlock(workspace, `${PREFIX}_divergence_v_scheme_dropdown`);
      if (interpolationBlock) {
        const field = interpolationBlock.getField('V_SCHEME');
        if (field) {
          field.setValue(divergenceScheme.interpolationScheme);
        }
      }
    } else {
      // Use regular interpolation dropdown
      interpolationBlock = createBlock(workspace, `${PREFIX}_divergence_interpolation_dropdown`);
      if (interpolationBlock) {
        const field = interpolationBlock.getField('INTERPOLATION');
        if (field) {
          field.setValue(divergenceScheme.interpolationScheme);
        }
      }
    }
    
    if (interpolationBlock) {
      const input = divergenceBlock.getInput(inputNames[currentInputIndex]);
      if (input && input.connection) {
        input.connection.connect(interpolationBlock.outputConnection);
        console.log(`Connected divergence interpolation scheme block: ${divergenceScheme.interpolationScheme} (limited: ${divergenceScheme.isLimited}, V-scheme: ${divergenceScheme.isVScheme})`);
      }
    }
    currentInputIndex++;
  }
  
  // Add gradient reference if present
  if (divergenceScheme.gradientReference && currentInputIndex < inputNames.length) {
    const gradientRefBlock = createBlock(workspace, `${PREFIX}_gradient_reference`);
    if (gradientRefBlock) {
      // Parse the gradient reference like "grad(U)" or "unlimitedGrad(p)"
      const match = divergenceScheme.gradientReference.match(/(\w+)\((\w+)\)/);
      if (match) {
        const functionField = gradientRefBlock.getField('FUNCTION');
        const variableField = gradientRefBlock.getField('VARIABLE');
        if (functionField && variableField) {
          functionField.setValue(match[1]); // 'grad' or 'unlimitedGrad'
          variableField.setValue(match[2]); // 'U', 'p', etc.
        }
      }
      const input = divergenceBlock.getInput(inputNames[currentInputIndex]);
      if (input && input.connection) {
        input.connection.connect(gradientRefBlock.outputConnection);
        console.log(`Connected gradient reference block: ${divergenceScheme.gradientReference}`);
      }
    }
    currentInputIndex++;
  }
  
  // Add coefficient if present
  if (divergenceScheme.coefficient !== null && currentInputIndex < inputNames.length) {
    const coefficientBlock = createBlock(workspace, `${PREFIX}_value_number`);
    if (coefficientBlock) {
      coefficientBlock.setFieldValue(divergenceScheme.coefficient, 'VALUE');
      const input = divergenceBlock.getInput(inputNames[currentInputIndex]);
      if (input && input.connection) {
        input.connection.connect(coefficientBlock.outputConnection);
        console.log(`Connected coefficient block: ${divergenceScheme.coefficient}`);
      }
    }
    currentInputIndex++;
  }
  
  return divergenceBlock;
}

// Create enhanced valueList blocks with proper construction from AST values
function createEnhancedValueListBlock(workspace, valueListComponent, key, path = '') {
  console.log(`Creating enhanced valueList block for key: ${key} at path: ${path}`, valueListComponent);
  
  if (!valueListComponent || !Array.isArray(valueListComponent.values)) {
    console.warn(`Invalid valueList component at path: ${path}:`, valueListComponent);
    return null;
  }
  const values = valueListComponent.values;
  console.log(`Constructing valueList with ${values.length} values at path: ${path}:`, values);
  
  // Check if this is a gradient scheme based on path or key
  const isGradientScheme = path.toLowerCase().includes('gradschemes') || 
                          (key && key.toLowerCase().includes('grad') && !key.toLowerCase().includes('sngrad'));
  
  // Check if this is a divergence scheme based on path or key
  const isDivergenceScheme = path.toLowerCase().includes('divschemes') || 
                            (key && key.toLowerCase().includes('div'));
  
  // Special handling for gradient schemes to match OpenFOAM structure:
  // grad(variable) <optional limiter> <gradient scheme> <interpolation scheme>
  if (isGradientScheme && values.length > 0) {
    console.log(`Processing gradient scheme with values:`, values);
    
    // Parse the gradient scheme structure
    const gradientScheme = parseGradientScheme(values);
    console.log(`Parsed gradient scheme structure:`, gradientScheme);
    
    // Create a specialized gradient block that follows OpenFOAM structure
    const gradientBlock = createGradientSchemeBlock(workspace, gradientScheme, key, path);
    if (gradientBlock) {
      console.log(`Successfully created structured gradient scheme block for key: ${key}`);
      return gradientBlock;
    } else {
      console.warn(`Failed to create structured gradient scheme block, falling back to standard processing`);
    }
  }
  
  // Special handling for divergence schemes to merge multi-part values:
  // <bounded> <scheme> <interpolation> <gradient_reference>
  if (isDivergenceScheme && values.length > 0) {
    console.log(`Processing divergence scheme with values:`, values);
    
    // Parse the divergence scheme structure with merging
    const divergenceScheme = parseDivergenceScheme(values);
    console.log(`Parsed divergence scheme structure:`, divergenceScheme);
    
    // Create a specialized divergence block that follows OpenFOAM structure
    const divergenceBlock = createDivergenceSchemeBlock(workspace, divergenceScheme, key, path);
    if (divergenceBlock) {
      console.log(`Successfully created structured divergence scheme block for key: ${key}`);
      return divergenceBlock;
    } else {
      console.warn(`Failed to create structured divergence scheme block, falling back to standard processing`);
    }
  }
  
  // Determine the appropriate specialized block type based on key/context and path
  let valueListBlockType = determineSpecializedValueBlockType(key, values, path);
  
  console.log(`Selected specialized block type: ${valueListBlockType} for key: ${key} at path: ${path}`);
  
  // Handle single values specially - if string block is selected, create it directly
  if (values.length === 1 && valueListBlockType === `${PREFIX}_value_string`) {
    console.log(`Single value creating string block at path: ${path}:`, values[0]);
    const stringBlock = createBlock(workspace, `${PREFIX}_value_string`);
    if (stringBlock) {
      stringBlock.setFieldValue(values[0] || '', 'VALUE');
    }
    return stringBlock;
  }
  
  // Handle empty valueLists
  if (values.length === 0) {
    console.warn(`Empty valueList for key: ${key} at path: ${path}`);
    return null;
  }
  
  const valueListBlock = createBlock(workspace, valueListBlockType);
  if (!valueListBlock) {
    console.error(`Failed to create specialized value block of type: ${valueListBlockType}`);
    return null;
  }
  
  console.log(`Created ${valueListBlockType} block, populating with actual values`);
  
  // For divergence schemes, use merged values if applicable
  let processedValues = values;
  if (isDivergenceScheme) {
    processedValues = mergeDivergenceSchemeValues(values);
    console.log(`Using merged divergence values for UI:`, processedValues);
  }
  
  // Populate the specialized block with actual values from AST (fill from beginning, leave rest empty)
  const inputNames = ['PART1', 'PART2', 'PART3', 'PART4', 'PART5'];
  
  processedValues.forEach((part, index) => {
    if (index < inputNames.length && valueListBlock.getInput(inputNames[index])) {
      console.log(`Processing value part ${index}: "${part}"`);
      
      // Check if this part is a number - if so, use simple text instead of dropdown
      const isNumeric = !isNaN(parseFloat(part)) && isFinite(part);
      
      if (isNumeric) {
        console.log(`Part "${part}" is numeric, using number text block`);
        
        // Use fvSchemes-specific number block for numeric values
        const numberBlock = createBlock(workspace, `${PREFIX}_value_number`);
        if (numberBlock) {
          numberBlock.setFieldValue(parseFloat(part), 'VALUE');
          console.log(`Created fvSchemes number block for numeric value: "${part}"`);
          
          // Connect to input
          const input = valueListBlock.getInput(inputNames[index]);
          if (input && input.connection) {
            input.connection.connect(numberBlock.outputConnection);
            console.log(`Connected fvSchemes number block to specialized value block input: ${inputNames[index]}`);
          }
        } else {
          console.error(`Failed to create fvSchemes number block for numeric value: "${part}"`);
        }
      } else {
        // Non-numeric values use dropdown logic
        let dropdownBlockType;
        let fieldName;
        let fieldValue = part;
        
        // Enhanced logic to match scheme components based on position and content for gradient schemes
        if (isGradientScheme) {
          if (index === 0) {
            // First part: check if it's a limiter or gradient scheme
            if (['cellLimited', 'cellMDLimited', 'faceLimited', 'faceMDLimited', 'clippedLinear'].includes(part)) {
              dropdownBlockType = `${PREFIX}_gradient_limiter_dropdown`;
              fieldName = 'LIMITER';
            } else {
              // Gradient scheme (Gauss, leastSquares)
              dropdownBlockType = `${PREFIX}_gradient_scheme_dropdown`;
              fieldName = 'GRADIENT_SCHEME';
            }
          } else if (index === 1) {
            // Second part: could be gradient scheme if first was limiter, or interpolation if first was scheme
            if (['Gauss', 'leastSquares'].includes(part)) {
              dropdownBlockType = `${PREFIX}_gradient_scheme_dropdown`;
              fieldName = 'GRADIENT_SCHEME';
            } else {
              // Interpolation scheme
              dropdownBlockType = `${PREFIX}_gradient_interpolation_dropdown`;
              fieldName = 'INTERPOLATION';
            }
          } else if (index === 2) {
            // Third part: usually interpolation scheme for gradients
            dropdownBlockType = `${PREFIX}_gradient_interpolation_dropdown`;
            fieldName = 'INTERPOLATION';
          } else {
            // Later parts: default to interpolation
            dropdownBlockType = `${PREFIX}_gradient_interpolation_dropdown`;
            fieldName = 'INTERPOLATION';
          }
        } else if (isDivergenceScheme) {
          // Enhanced divergence scheme logic with merged value support
          if (index === 0) {
            // First part is the main divergence scheme type (could be merged like "bounded Gauss")
            dropdownBlockType = `${PREFIX}_divergence_scheme_dropdown`;
            fieldName = 'DIVERGENCE_SCHEME';
          } else if (index === 1) {
            // Second part is usually interpolation method
            if (part.startsWith('limited')) {
              dropdownBlockType = `${PREFIX}_divergence_limited_dropdown`;
              fieldName = 'LIMITED_SCHEME';
              // Extract the scheme name from 'limitedLinear' -> 'Linear'
              fieldValue = part.replace('limited', '');
              fieldValue = fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1);
            } else if (part.endsWith('V')) {
              dropdownBlockType = `${PREFIX}_divergence_v_scheme_dropdown`;
              fieldName = 'V_SCHEME';
              // Extract the base scheme from 'linearV' -> 'linear'
              fieldValue = part.replace('V', '');
            } else {
              dropdownBlockType = `${PREFIX}_divergence_interpolation_dropdown`;
              fieldName = 'INTERPOLATION';
            }
          } else if (index === 2) {
            // Third part could be gradient reference for divergence
            if (part.includes('grad(') || part.includes('unlimitedGrad(')) {
              // Gradient function reference
              dropdownBlockType = `${PREFIX}_gradient_reference`;
              fieldName = 'FUNCTION';
              // Extract function and variable from grad(U) format
              const match = part.match(/(\w+)\((\w+)\)/);
              if (match) {
                fieldValue = match[1]; // 'grad' or 'unlimitedGrad'
              }
            } else {
              dropdownBlockType = `${PREFIX}_divergence_interpolation_dropdown`;
              fieldName = 'INTERPOLATION';
            }
          } else {
            // Default fallback for additional parts
            dropdownBlockType = `${PREFIX}_divergence_interpolation_dropdown`;
            fieldName = 'INTERPOLATION';
          }
        } else {
          // Non-gradient/divergence schemes: use context-aware selection based on path and position
          if (index === 0) {
            // First part is usually the main scheme type
            if (path.toLowerCase().includes('ddtschemes') || path.toLowerCase().includes('time')) {
              dropdownBlockType = `${PREFIX}_time_scheme_dropdown`;
              fieldName = 'TIME_SCHEME';
            } else {
              // Default to gradient scheme for unknown contexts
              dropdownBlockType = `${PREFIX}_gradient_scheme_dropdown`;
              fieldName = 'GRADIENT_SCHEME';
            }
          } else if (index === 1) {
            // Second part is usually interpolation method
            dropdownBlockType = `${PREFIX}_gradient_interpolation_dropdown`;
            fieldName = 'INTERPOLATION';
          } else if (index === 2) {
            // Third part could be corrector for laplacian schemes
            if (path.toLowerCase().includes('laplacianschemes') || path.toLowerCase().includes('laplacian')) {
              dropdownBlockType = `${PREFIX}_laplacian_corrector_dropdown`;
              fieldName = 'CORRECTOR';
            } else {
              // Default to interpolation
              dropdownBlockType = `${PREFIX}_gradient_interpolation_dropdown`;
              fieldName = 'INTERPOLATION';
            }
          } else if (index === 3) {
            // Fourth part - usually corrector for laplacian schemes
            if (path.toLowerCase().includes('laplacianschemes') || path.toLowerCase().includes('laplacian')) {
              dropdownBlockType = `${PREFIX}_laplacian_corrector_dropdown`;
              fieldName = 'CORRECTOR';
            } else {
              dropdownBlockType = `${PREFIX}_gradient_interpolation_dropdown`;
              fieldName = 'INTERPOLATION';
            }
          } else {
            // Default fallback for additional parts
            dropdownBlockType = `${PREFIX}_gradient_interpolation_dropdown`;
            fieldName = 'INTERPOLATION';
          }
        }
        
        const dropdownBlock = createBlock(workspace, dropdownBlockType);
        if (dropdownBlock) {
          console.log(`Created specialized dropdown block ${dropdownBlockType} for part "${part}"`);
          
          // Set the field value if the field exists
          const field = dropdownBlock.getField(fieldName);
          if (field) {
            // Check if the value is valid for this dropdown
            const options = field.getOptions();
            const validOption = options.find(option => option[1] === fieldValue);
            if (validOption) {
              field.setValue(fieldValue);
              console.log(`Set dropdown field ${fieldName} to actual value: "${fieldValue}"`);
            } else {
              // Use the first option as fallback but warn
              field.setValue(options[0][1]);
              console.warn(`Value "${fieldValue}" not found in dropdown options for ${fieldName}, using fallback: ${options[0][1]}`);
            }
          }
          
          // Handle gradient variable for gradient reference blocks
          if (dropdownBlockType === `${PREFIX}_gradient_reference` && part.includes('grad(')) {
            const match = part.match(/(\w+)\((\w+)\)/);
            if (match) {
              const variableField = dropdownBlock.getField('VARIABLE');
              if (variableField) {
                variableField.setValue(match[2]); // Set the variable (U, p, etc.)
                console.log(`Set gradient variable field to: "${match[2]}"`);
              }
            }
          }
          
          // Connect to input
          const input = valueListBlock.getInput(inputNames[index]);
          if (input && input.connection) {
            input.connection.connect(dropdownBlock.outputConnection);
            console.log(`Connected specialized dropdown block to value block input: ${inputNames[index]}`);
          }
        } else {
          console.error(`Failed to create specialized dropdown block of type: ${dropdownBlockType}`);
        }
      }
    }
  });
  
  // Log which inputs were left empty (expected behavior)
  inputNames.forEach((inputName, index) => {
    if (index >= processedValues.length) {
      const input = valueListBlock.getInput(inputName);
      if (input && !input.connection.isConnected()) {
        console.log(`Input ${inputName} left empty as expected (only ${processedValues.length} values provided)`);
      }
    }
  });
  
  console.log(`Successfully constructed specialized ${valueListBlockType} block for key: ${key}`);
  return valueListBlock;
}

/**
 * Parse gradient scheme values to extract OpenFOAM gradient scheme structure
 * Expected format: <optional limiter> <gradient scheme> <interpolation scheme>
 * e.g., ["cellLimited", "Gauss", "linear", "1"] or ["Gauss", "linear"]
 */
function parseGradientScheme(values) {
  const scheme = {
    limiter: null,
    gradientScheme: null,
    interpolationScheme: null,
    coefficient: null
  };
  
  if (!values || values.length === 0) {
    return scheme;
  }
  
  // Valid OpenFOAM gradient limiters
  const limiters = ['cellLimited', 'cellMDLimited', 'faceLimited', 'faceMDLimited', 'clippedLinear'];
  // Valid OpenFOAM gradient schemes
  const gradientSchemes = ['Gauss', 'leastSquares'];
  // Valid OpenFOAM interpolation schemes for gradients
  const interpolationSchemes = ['linear', 'pointLinear', 'leastSquares'];
  
  let currentIndex = 0;
  
  // Check for optional limiter (usually first if present)
  if (currentIndex < values.length && limiters.includes(values[currentIndex])) {
    scheme.limiter = values[currentIndex];
    currentIndex++;
  }
  
  // Check for gradient scheme (required)
  if (currentIndex < values.length && gradientSchemes.includes(values[currentIndex])) {
    scheme.gradientScheme = values[currentIndex];
    currentIndex++;
  } else if (currentIndex < values.length) {
    // If not a known gradient scheme, assume it's still the gradient scheme
    scheme.gradientScheme = values[currentIndex];
    currentIndex++;
  }
  
  // Check for interpolation scheme
  if (currentIndex < values.length && interpolationSchemes.includes(values[currentIndex])) {
    scheme.interpolationScheme = values[currentIndex];
    currentIndex++;
  } else if (currentIndex < values.length) {
    // If not a known interpolation scheme, assume it's still the interpolation scheme
    scheme.interpolationScheme = values[currentIndex];
    currentIndex++;
  }
  
  // Check for coefficient (usually numeric, at the end)
  if (currentIndex < values.length) {
    const lastValue = values[currentIndex];
    if (!isNaN(parseFloat(lastValue)) && isFinite(lastValue)) {
      scheme.coefficient = parseFloat(lastValue);
    }
  }
  
  console.log(`Parsed gradient scheme:`, scheme);
  return scheme;
}

/**
 * Create a structured gradient scheme block following OpenFOAM format
 */
function createGradientSchemeBlock(workspace, gradientScheme, key, path) {
  console.log(`Creating structured gradient scheme block:`, gradientScheme);
  
  // Create the main gradient value block
  const gradientBlock = createBlock(workspace, `${PREFIX}_gradient_value`);
  if (!gradientBlock) {
    console.error(`Failed to create gradient value block`);
    return null;
  }
  
  const inputNames = ['PART1', 'PART2', 'PART3', 'PART4', 'PART5'];
  let currentInputIndex = 0;
  
  // Add limiter if present
  if (gradientScheme.limiter && currentInputIndex < inputNames.length) {
    const limiterBlock = createBlock(workspace, `${PREFIX}_gradient_limiter_dropdown`);
    if (limiterBlock) {
      const field = limiterBlock.getField('LIMITER');
      if (field) {
        field.setValue(gradientScheme.limiter);
      }
      const input = gradientBlock.getInput(inputNames[currentInputIndex]);
      if (input && input.connection) {
        input.connection.connect(limiterBlock.outputConnection);
        console.log(`Connected gradient limiter block: ${gradientScheme.limiter}`);
      }
    }
    currentInputIndex++;
  }
  
  // Add gradient scheme (required)
  if (gradientScheme.gradientScheme && currentInputIndex < inputNames.length) {
    const schemeBlock = createBlock(workspace, `${PREFIX}_gradient_scheme_dropdown`);
    if (schemeBlock) {
      const field = schemeBlock.getField('GRADIENT_SCHEME');
      if (field) {
        field.setValue(gradientScheme.gradientScheme);
      }
      const input = gradientBlock.getInput(inputNames[currentInputIndex]);
      if (input && input.connection) {
        input.connection.connect(schemeBlock.outputConnection);
        console.log(`Connected gradient scheme block: ${gradientScheme.gradientScheme}`);
      }
    }
    currentInputIndex++;
  }
  
  // Add interpolation scheme if present
  if (gradientScheme.interpolationScheme && currentInputIndex < inputNames.length) {
    const interpolationBlock = createBlock(workspace, `${PREFIX}_gradient_interpolation_dropdown`);
    if (interpolationBlock) {
      const field = interpolationBlock.getField('INTERPOLATION');
      if (field) {
        field.setValue(gradientScheme.interpolationScheme);
      }
      const input = gradientBlock.getInput(inputNames[currentInputIndex]);
      if (input && input.connection) {
        input.connection.connect(interpolationBlock.outputConnection);
        console.log(`Connected gradient interpolation scheme block: ${gradientScheme.interpolationScheme}`);
      }
    }
    currentInputIndex++;
  }
  
  // Add coefficient if present
  if (gradientScheme.coefficient !== null && currentInputIndex < inputNames.length) {
    const coefficientBlock = createBlock(workspace, `${PREFIX}_value_number`);
    if (coefficientBlock) {
      coefficientBlock.setFieldValue(gradientScheme.coefficient, 'VALUE');
      const input = gradientBlock.getInput(inputNames[currentInputIndex]);
      if (input && input.connection) {
        input.connection.connect(coefficientBlock.outputConnection);
        console.log(`Connected coefficient block: ${gradientScheme.coefficient}`);
      }
    }
    currentInputIndex++;
  }
  
  return gradientBlock;
}

/**
 * Determine the appropriate specialized value block type based on key, context, and path
 */
function determineSpecializedValueBlockType(key, values, path = '') {
  console.log(`Determining specialized block type for key: ${key}, values:`, values, `at path: ${path}`);
  
  // Parse the path to extract scheme section information
  const pathLower = path.toLowerCase();
  console.log(`Analyzing path: ${path} (lowercase: ${pathLower})`);
  
  // Extract scheme section from path patterns like "fvSchemes_gradSchemes_something_value"
  const schemePatterns = {
    // Gradient schemes patterns
    gradSchemes: [
      /fvschemes.*gradschemes/i,
      /.*_gradschemes_/i,
      /_grad_/i,
      /gradient/i
    ],
    
    // Divergence schemes patterns  
    divSchemes: [
      /fvschemes.*divschemes/i,
      /.*_divschemes_/i,
      /_div_/i,
      /divergence/i
    ],
    
    // Time derivative schemes patterns
    ddtSchemes: [
      /fvschemes.*ddtschemes/i,
      /.*_ddtschemes_/i,
      /_ddt_/i,
      /time.*derivative/i
    ],
    
    // Second time derivative schemes patterns
    d2dt2Schemes: [
      /fvschemes.*d2dt2schemes/i,
      /.*_d2dt2schemes_/i,
      /_d2dt2_/i,
      /second.*time.*derivative/i
    ],
    
    // Laplacian schemes patterns
    laplacianSchemes: [
      /fvschemes.*laplacianschemes/i,
      /.*_laplacianschemes_/i,
      /_laplacian_/i,
      /laplacian/i
    ],
    
    // Interpolation schemes patterns
    interpolationSchemes: [
      /fvschemes.*interpolationschemes/i,
      /.*_interpolationschemes_/i,
      /_interpolation_/i,
      /interpolation/i
    ],
    
    // Surface normal gradient schemes patterns
    snGradSchemes: [
      /fvschemes.*sngradschemes/i,
      /.*_sngradschemes_/i,
      /_sngrad_/i,
      /surface.*normal.*gradient/i
    ]
  };
  
  // Check each scheme pattern against the path
  for (const [schemeName, patterns] of Object.entries(schemePatterns)) {
    for (const pattern of patterns) {
      if (pattern.test(pathLower)) {
        console.log(`Path matches ${schemeName} pattern: ${pattern} at path: ${path}`);
        
        // Map scheme names to their corresponding specialized value blocks
        const schemeToBlockType = {
          gradSchemes: `${PREFIX}_gradient_value`,
          divSchemes: `${PREFIX}_divergence_value`, 
          ddtSchemes: `${PREFIX}_time_derivative_value`,
          d2dt2Schemes: `${PREFIX}_second_time_derivative_value`,
          laplacianSchemes: `${PREFIX}_laplacian_value`,
          interpolationSchemes: `${PREFIX}_value_string`, // Simple string for interpolation
          snGradSchemes: `${PREFIX}_value_string` // Simple string for snGrad
        };
        
        const blockType = schemeToBlockType[schemeName];
        console.log(`Selected specialized block type: ${blockType} for scheme: ${schemeName} at path: ${path}`);
        return blockType;
      }
    }
  }
  
  console.log(`No scheme section detected in path, checking key and values for fallback determination at path: ${path}`);
  
  // Fallback: Check the key parameter for additional clues
  if (key) {
    const keyLower = key.toLowerCase();
    
    if (keyLower.includes('grad') && !keyLower.includes('sngrad')) {
      console.log(`Key ${key} contains 'grad', using gradient value block at path: ${path}`);
      return `${PREFIX}_gradient_value`;
    }
    
    if (keyLower.includes('div')) {
      console.log(`Key ${key} contains 'div', using divergence value block at path: ${path}`);
      return `${PREFIX}_divergence_value`;
    }
    
    if (keyLower.includes('ddt') || keyLower.includes('time')) {
      console.log(`Key ${key} contains 'ddt' or 'time', using time derivative value block at path: ${path}`);
      return `${PREFIX}_time_derivative_value`;
    }
    
    if (keyLower.includes('d2dt2')) {
      console.log(`Key ${key} contains 'd2dt2', using second time derivative value block at path: ${path}`);
      return `${PREFIX}_second_time_derivative_value`;
    }
    
    if (keyLower.includes('laplacian')) {
      console.log(`Key ${key} contains 'laplacian', using laplacian value block at path: ${path}`);
      return `${PREFIX}_laplacian_value`;
    }
    
    if (keyLower.includes('sngrad')) {
      console.log(`Key ${key} contains 'sngrad', using simple string value block at path: ${path}`);
      return `${PREFIX}_value_string`;
    }
    
    if (keyLower.includes('interpolat')) {
      console.log(`Key ${key} contains 'interpolat', using simple string value block at path: ${path}`);
      return `${PREFIX}_value_string`;
    }
  }
  
  // Analyze values to determine appropriate block type as last resort
  if (values && values.length > 0) {
    const firstValue = values[0];
    
    // Time scheme indicators
    if (['steadyState', 'Euler', 'backward', 'CrankNicolson', 'localEuler'].includes(firstValue)) {
      console.log(`First value ${firstValue} indicates time derivative scheme at path: ${path}`);
      return `${PREFIX}_time_derivative_value`;
    }
    
    // Gradient scheme indicators  
    if (['Gauss', 'leastSquares', 'cellLimited'].includes(firstValue) || firstValue.includes('Grad')) {
      console.log(`First value ${firstValue} indicates gradient scheme at path: ${path}`);
      return `${PREFIX}_gradient_value`;
    }
    
    // Divergence scheme indicators
    if (firstValue === 'bounded' || (firstValue === 'Gauss' && values.length > 2)) {
      console.log(`Values pattern indicates divergence scheme at path: ${path}`);
      return `${PREFIX}_divergence_value`;
    }
    
    // Laplacian scheme indicators (typically "Gauss linear corrected" etc.)
    if (firstValue === 'Gauss' && values.length >= 3 && 
        (values[2] === 'corrected' || values[2] === 'limited' || values[2] === 'orthogonal')) {
      console.log(`Values pattern indicates laplacian scheme at path: ${path}`);
      return `${PREFIX}_laplacian_value`;
    }
  }
  
  // Final fallback - use gradient value block for multi-part values, string for single values
  if (values && values.length > 1) {
    console.log(`Multi-part value, defaulting to gradient value block at path: ${path}`);
    return `${PREFIX}_gradient_value`;
  } else {
    console.log(`Single or no values, using simple string block at path: ${path}`);
    return `${PREFIX}_value_string`;
  }
}

// Register dropdown blocks for different scheme values
function registerSchemeValueDropdowns(filename) {
  // Gradient schemes dropdown - only valid gradient calculation methods
  const gradientSchemeId = `${PREFIX}_gradient_scheme_dropdown`;
  if (!Blockly.Blocks[gradientSchemeId]) {
    Blockly.Blocks[gradientSchemeId] = {
      init: function() {
        this.jsonInit({
          type: gradientSchemeId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'GRADIENT_SCHEME',
              options: [
                ['Gauss', 'Gauss'],
                ['leastSquares', 'leastSquares']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.GRADIENT,
          tooltip: 'Gradient calculation scheme (Gauss or leastSquares)',
          editable: true
        });
      }
    };
  }

  // Interpolation schemes dropdown - only valid interpolation methods for gradients
  const gradientInterpolationId = `${PREFIX}_gradient_interpolation_dropdown`;
  if (!Blockly.Blocks[gradientInterpolationId]) {
    Blockly.Blocks[gradientInterpolationId] = {
      init: function() {
        this.jsonInit({
          type: gradientInterpolationId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'INTERPOLATION',
              options: [
                ['linear', 'linear'],
                ['pointLinear', 'pointLinear'],
                ['leastSquares', 'leastSquares']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.INTERPOLATION,
          tooltip: 'Interpolation scheme (linear: cell-based, pointLinear: point-based, leastSquares)',
          editable: true
        });
      }
    };
  }

  // Gradient limiters dropdown - only valid gradient limiters
  const gradientLimiterId = `${PREFIX}_gradient_limiter_dropdown`;
  if (!Blockly.Blocks[gradientLimiterId]) {
    Blockly.Blocks[gradientLimiterId] = {
      init: function() {
        this.jsonInit({
          type: gradientLimiterId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'LIMITER',
              options: [
                ['cellLimited', 'cellLimited'],
                ['cellMDLimited', 'cellMDLimited'],
                ['faceLimited', 'faceLimited'],
                ['faceMDLimited', 'faceMDLimited'],
                ['clippedLinear', 'clippedLinear']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.GRADIENT,
          tooltip: 'Gradient limiter for monotonicity preservation (cell/face limited schemes and clippedLinear)',
          editable: true
        });
      }
    };
  }

  // Divergence scheme type dropdown - for divergence schemes only
  const divergenceSchemeId = `${PREFIX}_divergence_scheme_dropdown`;
  if (!Blockly.Blocks[divergenceSchemeId]) {
    Blockly.Blocks[divergenceSchemeId] = {
      init: function() {
        this.jsonInit({
          type: divergenceSchemeId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'DIVERGENCE_SCHEME',
              options: [
                ['Gauss', 'Gauss'],
                ['bounded Gauss', 'bounded Gauss']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.DIVERGENCE,
          tooltip: 'Divergence scheme type (Gauss or bounded Gauss for steady state)',
          editable: true
        });
      }
    };
  }

  // Divergence interpolation dropdown - NVD/TVD and Non-NVD/TVD schemes
  const divergenceInterpolationId = `${PREFIX}_divergence_interpolation_dropdown`;
  if (!Blockly.Blocks[divergenceInterpolationId]) {
    Blockly.Blocks[divergenceInterpolationId] = {
      init: function() {
        this.jsonInit({
          type: divergenceInterpolationId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'INTERPOLATION',
              options: [
                // NVD/TVD convection schemes
                ['linear', 'linear'],
                ['limitedLinear', 'limitedLinear'],
                ['linearUpwind', 'linearUpwind'],
                ['upwind', 'upwind'],
                ['vanLeer', 'vanLeer'],
                ['QUICK', 'QUICK'],
                ['UMIST', 'UMIST'],
                ['MUSCL', 'MUSCL'],
                ['minmod', 'minmod'],
                ['midPoint', 'midPoint'],
                // Non-NVD/TVD convection schemes
                ['LUST', 'LUST'],
                ['filteredLinear2', 'filteredLinear2'],
                ['CBlended', 'CBlended'],
                ['DESHybrid', 'DESHybrid']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.DIVERGENCE,
          tooltip: 'Interpolation scheme for divergence (NVD/TVD and Non-NVD/TVD schemes)',
          editable: true
        });
      }
    };
  }

  // Divergence limited interpolation dropdown - for bounded scalars
  const divergenceLimitedId = `${PREFIX}_divergence_limited_dropdown`;
  if (!Blockly.Blocks[divergenceLimitedId]) {
    Blockly.Blocks[divergenceLimitedId] = {
      init: function() {
        this.jsonInit({
          type: divergenceLimitedId,
          message0: 'limited%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'LIMITED_SCHEME',
              options: [
                ['Linear', 'Linear'],
                ['VanLeer', 'VanLeer'],
                ['QUICK', 'QUICK'],
                ['UMIST', 'UMIST'],
                ['MUSCL', 'MUSCL'],
                ['Minmod', 'Minmod']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.DIVERGENCE,
          tooltip: 'Limited interpolation scheme for bounded scalars',
          editable: true
        });
      }
    };
  }

  // Divergence V-schemes dropdown - for vector fields
  const divergenceVSchemeId = `${PREFIX}_divergence_v_scheme_dropdown`;
  if (!Blockly.Blocks[divergenceVSchemeId]) {
    Blockly.Blocks[divergenceVSchemeId] = {
      init: function() {
        this.jsonInit({
          type: divergenceVSchemeId,
          message0: '%1V',
          args0: [
            {
              type: 'field_dropdown',
              name: 'V_SCHEME',
              options: [
                ['linear', 'linear'],
                ['limitedLinear', 'limitedLinear'],
                ['linearUpwind', 'linearUpwind'],
                ['vanLeer', 'vanLeer'],
                ['QUICK', 'QUICK'],
                ['UMIST', 'UMIST']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.DIVERGENCE,
          tooltip: 'V-scheme for vector fields (limiter applied in direction of greatest change)',
          editable: true
        });
      }
    };
  }

  // Time derivative schemes dropdown - for time schemes only
  const timeSchemeId = `${PREFIX}_time_scheme_dropdown`;
  if (!Blockly.Blocks[timeSchemeId]) {
    Blockly.Blocks[timeSchemeId] = {
      init: function() {
        this.jsonInit({
          type: timeSchemeId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'TIME_SCHEME',
              options: [
                ['steadyState', 'steadyState'],
                ['Euler', 'Euler'],
                ['backward', 'backward'],
                ['CrankNicolson', 'CrankNicolson'],
                ['localEuler', 'localEuler']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.TIME_DERIV,
          tooltip: 'Time derivative scheme',
          editable: true
        });
      }
    };
  }

  // Laplacian corrector dropdown - for laplacian schemes
  const laplacianCorrectorId = `${PREFIX}_laplacian_corrector_dropdown`;
  if (!Blockly.Blocks[laplacianCorrectorId]) {
    Blockly.Blocks[laplacianCorrectorId] = {
      init: function() {
        this.jsonInit({
          type: laplacianCorrectorId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'CORRECTOR',
              options: [
                ['corrected', 'corrected'],
                ['limited corrected', 'limited corrected'],
                ['orthogonal', 'orthogonal'],
                ['uncorrected', 'uncorrected']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.LAPLACIAN,
          tooltip: 'Laplacian corrector method',
          editable: true
        });
      }
    };
  }

  // Coefficient dropdown for numeric coefficients
  const coefficientId = `${PREFIX}_coefficient_dropdown`;
  if (!Blockly.Blocks[coefficientId]) {
    Blockly.Blocks[coefficientId] = {
      init: function() {
        this.jsonInit({
          type: coefficientId,
          message0: '%1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'COEFFICIENT',
              options: [
                ['0.33', '0.33'],
                ['0.5', '0.5'],
                ['1', '1'],
                ['0.9', '0.9'],
                ['custom', 'custom']
              ]
            }
          ],
          output: 'Number',
          colour: COLORS.DROPDOWN,
          tooltip: 'Scheme coefficient (common values)',
          editable: true
        });
      }
    };
  }

  // Keep the gradient reference dropdown for function calls like grad(U)
  const gradientReferenceId = `${PREFIX}_gradient_reference`;
  if (!Blockly.Blocks[gradientReferenceId]) {
    Blockly.Blocks[gradientReferenceId] = {
      init: function() {
        this.jsonInit({
          type: gradientReferenceId,
          message0: '%1(%2)',
          args0: [
            {
              type: 'field_dropdown',
              name: 'FUNCTION',
              options: [
                ['grad', 'grad'],
                ['unlimitedGrad', 'unlimitedGrad']
              ]
            },
            {
              type: 'field_dropdown',
              name: 'VARIABLE',
              options: [
                ['U', 'U'],
                ['p', 'p'],
                ['k', 'k'],
                ['epsilon', 'epsilon'],
                ['omega', 'omega'],
                ['T', 'T'],
                ['h', 'h'],
                ['rho', 'rho'],
                ['nuTilda', 'nuTilda']
              ]
            }
          ],
          output: 'String',
          colour: COLORS.DROPDOWN,
          tooltip: 'Gradient function reference (e.g., grad(U))',
          editable: true
        });
      }
    };
  }
}